import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Inject,
  ViewChild,
  OnChanges,
  SimpleChanges,
  ElementRef,
  EventEmitter,
  ChangeDetectorRef,
  AfterContentChecked,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  combineLatest,
  merge,
  of,
  throwError,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  first,
  map,
  pairwise,
  share,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { ModalRef, ModalService } from '@purespectrum1/ui/modal';
import { DatePipe } from '@angular/common';
import * as moment from 'moment-timezone';

import { BuyerApiService } from '../shared/services/buyer-api/survey.service';
import { BuyerEntryLinkViewModalComponent } from './buyer-entry-link-view-modal/buyer-entry-link-view-modal.component';
import { CsvToJsonService } from '../shared/services/common-service/csv-to-json.service';
import { XlsxToJsonService } from '../shared/services/common-service/xlsx-to-json.service';
import { AuthService } from '../shared/services/auth/auth.service';
import { BuyerConfigService } from '../create-surveys/services/buyer-config/buyer.config.service';
import { Constants } from '../shared/constants/buyer-constant';
import {
  Quotas,
  Criteria,
  Qbp,
  PsidsBuyerUploadListResponse,
  PSIDs,
  PsidsUploadRefListResponse,
  NcSettings,
  PatchSurveyInterface,
  UniqueLinks,
} from '../shared/interfaces/survey.interface';
import {
  TimeZone,
  SelectDate,
  SelectTime,
  FilterDateRange,
} from '../shared/interfaces/date';

import { BuyerSettingsService } from '../shared/services/buyer-settings-service/buyer-settings-service.service';
import { notifyMessage } from '../shared/constants/notify-message';
import { GlobalConstants } from '../shared/constants/global.constants';
import { SURVEY_STATUS } from '../shared/constants/survey-status';
import { SurveyMetaDataService } from '../shared/services/survey-meta-data/survey-meta-data.service';
import { STEPPER_BUTTON_STATUS } from '../shared/constants/stepper-button-status';
import { SharedDataService } from '../shared/services/shared-data-service/shared-data-service';
import {
  IncludePostPayload,
  IncludePsidApiResponse,
  SurveyPayload,
} from '../shared/interfaces/survey-meta-data.interface';
import { SurveyStatus } from '../shared/utils/survey-status';
import { ClearOrViewPsidModalComponent } from './clear-or-view-psid-modal/clear-or-view-psid-modal.component';
import { EventEmitterService } from '../shared/services/event-emitter/event-emitter.service';
import { SurveyPayloadService } from '../create-surveys/survey-payload.service';
import { UserService } from '../shared/services/user-service/user.service';
import { ProjectManagers } from '../shared/interfaces/user.interface';
import {
  BuyerSurveyResponse,
  ClearPsidsResponse,
} from '../shared/interfaces/survey.interface';
import { ConfirmationModalComponent } from '../shared/ui/confirmation-modal/confirmation-modal.component';
import { TemplateService } from '../shared/services/template/template.service';
import { TemplateResponse } from '../create-surveys/layout/template-modal/template-modal.interface';
import { AuthState } from '../shared/interfaces/auth.interface';
import { EnvironmentConfig } from '../shared/interfaces/environment.interface';
import { MarketplaceService } from '../create-surveys/marketplace.service';
import { FooterComponent } from '../create-surveys/layout/footer/footer.component';
import { ActivatedRoute, Router } from '@angular/router';
import { SurveyLaunch } from '../shared/interfaces/survey-launch.interface';
import { IncludeListComponent } from './include-list/include-list.component';
import {
  DateRange,
  DaterangepickerOption,
  LocaleLanguageCountry,
} from '../create-surveys/shared/interfaces/feasibility.interface';
import { CommonService } from '../shared/services/common-service/common-service';
import { GetBuyerConfigResponse } from '../create-surveys/shared/interfaces/operator-settings.interface';
import { DaterangepickerComponent } from 'ng2-daterangepicker';
import { SpaceValidator } from '../shared/validators/survey/space-validator';
import { BillingRulesService } from '../shared/services/billing-rules/billing-rules-service';
import { BillingNumberRules } from '../shared/interfaces/billing-rules.interfaces';
import { SurveyUpdateError } from '../shared/services/buyer-api/domain/survey-update-error';
import { MissingMarkersModalComponent } from '../shared/ui/missing-markers-modal/missing-markers-modal.component';
import { SurveyNavigationState } from '../shared/interfaces/create-surveys.interface';
import { MultiCountryDraftService } from '../create-surveys/services/multi-country-draft/multi-country-draft.service';
import { MultiCountrySurvey } from '../create-surveys/shared/ui/multicountry/interface/multi-country.interface';
import { McSvpParentChildSegregator } from '../shared/domain/mc-svp-parent-child-segregator';
import { McSameSettingsDecisionMaker } from '../create-surveys/shared/ui/multicountry/domain/mc-same-settings-decision-maker';
import { SURVEY_LAUNCH_CONSTANTS } from '../shared/constants/survey-launch.constants';
import { SurveyRouteParamsComposer } from '../shared/utils/survey-route-params-composer';
import { SurveyStreamSegregator } from '../shared/utils/survey-stream-segregator';
import { McTraditionalSetterData } from '../create-surveys/shared/ui/multicountry/domain/mc-traditional-survey-settings-composer';
import { ActivityLogsService } from '../shared/services/activity-logs/activity-logs.service';
import {
  AidaPayload,
  AidaSurveyEligibility,
  SurveyFeaturesState,
} from '../shared/interfaces/aida.interface';
import { AidaService } from '../shared/services/aida/aida.service';
import {
  AidaMappedQualification,
  AidaPayloadService,
  AidaQualifications,
} from '../shared/services/aida/aida-payload/aida-payload.service';
import { QualificationsService } from '../create-surveys/services/qualification/qualifications.service';
import { Qualification } from '../shared/interfaces/qualification.interface';
import { Common } from '../shared/domain/common';

@Component({
  selector: 'ps-ui-survey-launch',
  templateUrl: './survey-launch.component.html',
  styleUrls: ['./survey-launch.component.css'],
})
export class SurveyLaunchComponent
  implements OnInit, OnDestroy, OnChanges, AfterContentChecked
{
  private readonly _baseUrl = this._env.environment.pureSpectrum.url;
  @Input() set auth(data: AuthState) {
    this._auth.setState(data);
  }
  @Input() surveyId: number;
  @Input() isInsight: boolean = false;
  @Input() finalizeInitialValues: Partial<SurveyLaunch> = {};
  @Input() disableLiveURLField!: boolean;
  @Input() disableTestURLField!: boolean;
  @ViewChild(FooterComponent) footer: FooterComponent;
  @ViewChild('dateRangeFilterRef', { read: DaterangepickerComponent })
  private _dateRangeFilterRef: DaterangepickerComponent;
  @ViewChild('basicToken') basicToken!: ElementRef;
  form!: FormGroup;
  private _missingMarkerModelRef: ModalRef<MissingMarkersModalComponent> | null =
    null;
  private _subscriptions: Subscription = new Subscription();
  public userType: string = '';
  public surveyDatails = <BuyerSurveyResponse>{};
  public userDetails!: any;
  public buyerEntryLinkUploaded: boolean = false;
  public buyerEntryLinksArr: Array<string> = [];
  public buyerEntryLinkInfo = Constants.FILE_LINKS;
  public warningTestFlg: boolean = false;
  public warningFlg: boolean = false;
  public showTransIdWarningMsg: boolean = false;
  public showNonHttpsWarningMsg: boolean = false;
  public testUrlCheck: boolean = false;
  public liveUrlCheck: boolean = false;
  public surveyGroupingBeforeUIChange!: number[];
  public checkWithUserUrl: string = '';
  public regexAmprsnd = new RegExp('\\&transaction_id', 'g');
  public regexQustionMrk = new RegExp('\\?transaction_id', 'g');
  public pmEmailFieldDisplay: boolean = false;
  public hours: SelectTime[] = [];
  public minutes: SelectTime[] = [];
  public tzNames: TimeZone[] = [];
  public qbpSelectionQuotas: QbpSelectionQuota[] = [];
  public qbpSelectedQuotaCategory: string = '';
  public feotFields = {
    feotQuantity: Constants.FEOT_INFO.DEFAULT_QUANTITY,
    feotRemainingTime: Constants.FEOT_INFO.DEFAULT_REMAINING_TIME,
  };
  public includePostPayload = {
    fromDate: '',
    toDate: '',
    surveyIds: [] as Array<number>,
  };
  public buyerSettings = {
    ps_psid: '',
    ps_status: '',
    ps_hash: '',
    ps_supplier_id: '',
    ps_transaction: '',
    ps_transaction_id2: '',
    ps_transaction_id3: '',
    ps_transaction_id4: '',
  };
  public scheduleLaunchSlideDisable: boolean = false;
  public scheduleLaunchDisable: boolean = false;
  public softLaunchDisable: boolean = false;
  public softLaunchSlideDisable: boolean = false;
  public fieldOverTimeDisable: boolean = false;
  public footerButtonStatus: number = STEPPER_BUTTON_STATUS.FINALIZE_AND_LAUNCH;
  public enableNightcrawler: boolean = false;
  public showDQBattery: boolean = false;
  public enableViewByPM: boolean = false;
  public isSurveySyncedOnNC: boolean = false;
  public ncSettings!: NcSettings | undefined;
  public showIncludeToo: boolean = false;
  public manageEdit: boolean = false;
  public inclExclDisable: boolean = true;
  public inclExclBuyerUpload: boolean = false;
  public isTraditionalMcSurvey: boolean = false;
  public isMcSvpSurvey: boolean = false;
  public isEnableMarginMaxForBuyer: boolean = false;
  public isEnableBillingNumberRulesForBuyer: boolean = false;
  private _psids: Array<PSIDs> = [];
  private _includePsids: string[] = [];
  public surveyGroupingPSIDUpload: boolean = false;
  public billingRules$: Observable<BillingNumberRules>;
  public billingRules?: BillingNumberRules;
  public showAdditionalSectionLabel: boolean = false;
  public aidaEligibility$: Observable<AidaEligibilityInfo>;

  fileName: string = '';
  file!: File;
  private _fdata: FormData = new FormData();
  public isQbpSliderDisabled: boolean = false;
  public isMultiNestingAvailable: boolean = false;
  public isQlcSliderDisabled: boolean = false;
  public surveyPayload: SurveyPayload = {} as SurveyPayload;
  public operatorProjectManagers: ProjectManagers[] = [];
  public buyerProjectManagers: ProjectManagers[] = [];
  public isServiceOperator: boolean = false;
  public isBuyerLinkSliderDisabled: boolean = false;
  public selectedTemplateResponse?: TemplateResponse;
  private _initDone = false;
  public buyerPmLabel: string = '-- Assign Project Manager --';
  public feotPacings = ['Hours', 'Days', 'Weeks'];
  public filterDateRange: DateRange = {};
  public filterByDatapickerOptions: DaterangepickerOption;
  public enableFilteringByDate: boolean = false;
  public surveyStatus = SurveyStatus;
  public notEditableBasicToken: boolean = true;
  public showSuccessTooltip: boolean = false;
  public fixedBasicTokenValue: string | undefined;
  public enableProjectLevelDataQuality: boolean = false;
  public enableMandatoryBillingNumber = false;
  public loggedInAsServiceBuyer = false;
  public isClickSurvey: boolean = false;
  public enableSupplierAllocationThrottling: boolean = false;

  public readonly SAME_SETTINGS_TEXT =
    SURVEY_LAUNCH_CONSTANTS.MULTICOUNTRY.CHECKBOX_BASED_SAME_SETTINGS_TEXT;
  public readonly AUTOMATIC_SAME_SETTINGS_TEXT =
    SURVEY_LAUNCH_CONSTANTS.MULTICOUNTRY.AUTOMATIC_SAME_SETTINGS_TEXT;
  public readonly SAME_SETTINGS_TOOLTIP_TEXT =
    SURVEY_LAUNCH_CONSTANTS.MULTICOUNTRY.SAME_SETTINGS_TOOLTIP_TEXT;
  public areSurveyDetailsHandlersInitialized = false;
  private _multicountrySurveys: MultiCountrySurvey[] = [];
  private _mcSpvParentSurvey?: BuyerSurveyResponse;

  private _locale: LocaleLanguageCountry;
  private _surveyCompanySubject = new Subject<number>();
  private _surveyIdCommand$ = new EventEmitter<number>();
  private _surveyDetailsCommand$ = new EventEmitter<BuyerSurveyResponse>();
  private _psIdsCommand$ = new EventEmitter<PSIDs[]>();
  private _buyerEntryLinksCommand$ = new EventEmitter<Array<string>>();
  private _surveyRouteParamsComposer!: SurveyRouteParamsComposer;
  private _allQualificationsData: Qualification[] = [];

  constructor(
    @Inject('ENV') private _env: EnvironmentConfig,
    private _router: Router,
    private _route: ActivatedRoute,
    private _fb: FormBuilder,
    private _buyerApiService: BuyerApiService,
    private _toastr: ToasterService,
    private _modal: ModalService,
    private _csvToJson: CsvToJsonService,
    private _auth: AuthService,
    private _buyerSettingsService: BuyerSettingsService,
    private _datepipe: DatePipe,
    private _surveyMetaDataService: SurveyMetaDataService,
    private _sharedDataService: SharedDataService,
    private _eventEmitterService: EventEmitterService,
    private _surveyPayloadService: SurveyPayloadService,
    private _userService: UserService,
    private _templateService: TemplateService,
    private _marketplaceService: MarketplaceService,
    private _commonService: CommonService,
    private _buyerConfigService: BuyerConfigService,
    private _billingRulesService: BillingRulesService,
    private _multiCountryDraftService: MultiCountryDraftService,
    public _activityLogService: ActivityLogsService,
    private _aidaService: AidaService,
    private _cd: ChangeDetectorRef,
    private _xlsxToJson: XlsxToJsonService,
    private _aidaPayloadService: AidaPayloadService,
    private _qualificationService: QualificationsService
  ) {
    this.userType = this._auth.userType;
    this._surveyRouteParamsComposer = new SurveyRouteParamsComposer(
      this._route,
      this._router
    );
  }

  ngOnInit(): void {
    this._subscriptions.add(
      this._auth.loggedIn$.subscribe((loggedIn: boolean) => {
        if (loggedIn) {
          this.userType = this._auth.userType;
        }
      })
    );
    this._subscriptions.add(
      this._auth.loggedInAsServiceBuyer$.subscribe((isLoggedIn) => {
        this.loggedInAsServiceBuyer = !!isLoggedIn;
      })
    );

    if (this.userType === 'operator') {
      this.buyerPmLabel = '-- Assign Buyer Project Manager --';
    }
    if (this._auth.buyerConfig?.includeToo) {
      this.enableFilteringByDate = true;
    }

    this.enableMandatoryBillingNumber =
      this._auth.buyerConfig?.enableMandatoryBillingNumber ?? false;

    this.isEnableBillingNumberRulesForBuyer =
      this._auth.buyerConfig?.enableBillingNumberRules ?? false;
    this.showAdditionalSectionLabel = this._shouldAdditionalSectionLabelShow();
    this._createDateRangePickerOption();
    this.userDetails = this._auth.user;
    this._getBuyerInfo(this.userDetails.cmp);
    this.showIncludeToo = this._checkFeatureAccess('includeToo');
    this.enableProjectLevelDataQuality =
      !!this._auth.buyerConfig?.enableProjectLevelDataQuality;
    this.showDQBattery = !!this._auth.buyerConfig?.enableSurveyDQBattery;
    this.enableNightcrawler = !!this._auth.buyerConfig?.enableNightcrawler;
    this.enableViewByPM = !!this._auth.buyerConfig?.enableViewByPM;

    if (!this.loggedInAsServiceBuyer && this._checkUserIsServiceOperator()) {
      this.enableProjectLevelDataQuality = true;
      this.showDQBattery = true;
    }

    this._generateForm();
    this._assignTimeField();
    this._subscriptions.add(
      this._sharedDataService.getPsidModelClose().subscribe((psidClose) => {
        this.inclExclBuyerUpload = psidClose.close;
      })
    );

    this._subscriptions.add(
      this._marketplaceService.invokeUpdateSurvey.subscribe(() => {
        if (!this.manageEdit) {
          // Live Edit
          this.footer?.editSurvey();
        } else {
          // draft
          this.footer?.save('save');
        }
      })
    );

    this._subscriptions.add(
      this._marketplaceService.invokeLaunchSurvey.subscribe(() => {
        if (this.manageEdit) {
          // draft
          this.footer?.launchSurvey();
        }
      })
    );

    this.enableMandatoryBillingNumber =
      this._auth.buyerConfig?.enableMandatoryBillingNumber ?? false;

    this._handleFormChangesForUiActivity();
    this._handleMcSurveyFormChanges();

    this._subscriptions.add(
      this._multiCountryDraftService
        .getSpvSurveyResponseData()
        .pipe(map((surveys) => new McSvpParentChildSegregator(surveys)))
        .subscribe((segregator) => {
          const childSurveys = segregator.childs();
          const parentSurvey = segregator.parent();
          this._mcSpvParentSurvey = parentSurvey;
          const testEntryLink = this.generateEntryLink('link');
          const umpire = new McSameSettingsDecisionMaker(childSurveys);
          const [childSurvey] = childSurveys;
          this._setEntryLinkItem(childSurveys);
          this.form.patchValue({
            survey_grouping: childSurvey.survey_grouping,
            billing_id: parentSurvey.billing_id,
            test_ps_survey_entry_link: testEntryLink,
            isSameBuyerUrlChecked: umpire.decide('buyer_url'),
            isSameBillingSurveyPoChecked: umpire.decide('billing_id'),
            isSameDqSettingsChecked: umpire.decide('dq_settings'),
            isSameLaunchSettingsChecked: umpire.decide('launch_settings'),
            isSameQuotaSettingsChecked: umpire.decide('quota_settings'),
            isSameFilterAudienceChecked: umpire.decide('filter_audience'),
            isSameEmailFeaturesChecked: umpire.decide('email_features'),
            isSameFileEntryLinksChecked: umpire.decide('unique_links'),
          });
        })
    );

    this._subscriptions.add(
      this._multiCountryDraftService
        .getMultiCountrySurveyData()
        .subscribe((data) => {
          this._multicountrySurveys = data;
        })
    );

    this.aidaEligibility$ = this._getAidaEligibility$();
    this._handleAidaDetails();
    this._subscriptions.add(
      this._multiCountryDraftService
        .getMcTraditionalSurveysResponse()
        .subscribe((data) => {
          const umpire = new McSameSettingsDecisionMaker(data);
          this.form.patchValue({
            isSameFileEntryLinksChecked: umpire.decide('unique_links'),
          });
          this.updatePayload({
            is_same_file_entry_links: umpire.decide('unique_links'),
          });
        })
    );
  }

  private _shouldAdditionalSectionLabelShow(): boolean {
    if (this._auth.userType === 'operator') {
      return true;
    }

    return !!this._auth.buyerConfig?.enableViewByPM;
  }

  public checkAndOpenMissingMarkersModal(surveyDetail: BuyerSurveyResponse) {
    if (this._missingMarkerModelRef) {
      return;
    }
    if (surveyDetail.mc_unique_id) {
      this._openMcMissingMarkersModal();
    } else {
      this._openMissingMarkerModal();
    }
  }

  private _openMcMissingMarkersModal() {
    // check against subject returning old/empty mc surveys the first time
    const surveyPresentInMcSurveys = (mcSurveys: MultiCountrySurvey[]) =>
      !!mcSurveys.find(
        (survey) => Number(survey.survey_id) === Number(this.surveyId)
      );

    this._subscriptions.add(
      this._multiCountryDraftService
        .getMultiCountrySurveyData()
        .pipe(first(surveyPresentInMcSurveys))
        .subscribe((mcSurveys) => {
          this._openMissingMarkerModal(mcSurveys);
        })
    );
  }

  private _openMissingMarkerModal(mcSurveys: MultiCountrySurvey[] = []) {
    this._missingMarkerModelRef = this._modal.open(
      MissingMarkersModalComponent,
      {
        data: {
          isCreated: true,
          displayLocales: this._returnDraftMCLocales(mcSurveys),
          psSurveyId: this._returnFirstMCSurvey(mcSurveys) || this.surveyId,
        },
      }
    );
  }

  private _returnDraftMCLocales(
    mcSurveys: MultiCountrySurvey[]
  ): string[] | null {
    if (!mcSurveys.length) {
      return null;
    }
    return mcSurveys
      .filter((mcSurvey) => mcSurvey.ps_survey_status == SURVEY_STATUS.DRAFT)
      .map((mcSurvey) => mcSurvey.survey_localization);
  }

  private _returnFirstMCSurvey(mcSurveys: MultiCountrySurvey[]): number | null {
    if (!mcSurveys.length) {
      return null;
    }
    const sortedSurveys = mcSurveys
      .map((survey) => survey.survey_id!)
      .sort((a, b) => a - b);
    return sortedSurveys[0];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.form) {
      this._generateForm();
    }
    if (!this.areSurveyDetailsHandlersInitialized) {
      this._handleSurveyDetails();
      this._handleSurveyIdChanges();
      this.areSurveyDetailsHandlersInitialized = true;
    }
    if (changes.surveyId?.currentValue) {
      this._surveyIdCommand$.next(this.surveyId);
    }
  }

  private _setBillingNumberRules(): void {
    const buyerCompanyId = this.surveyDatails.buyer_company || 0;

    this.billingRules$ = !this.isEnableBillingNumberRulesForBuyer
      ? EMPTY
      : this._billingRulesService.fetchRules(buyerCompanyId).pipe(
          tap((rules) => {
            this.billingRules = rules;

            if (!this.surveyDatails.billing_id) {
              const billingId =
                !this.billingRules.autoGenerate &&
                this.surveyDatails.ps_survey_status === SURVEY_STATUS.DRAFT
                  ? ''
                  : rules.example;
              this.patchBillingNumber(billingId);
              this.surveyDatails.billing_id = billingId;
            }
          })
        );
  }

  validateAndRemoveSpacesFromBillingNumber(billingNumber: string = '') {
    const validator = new SpaceValidator(billingNumber);
    if (validator.check()) {
      const updatedPo = validator.remove();
      this.patchBillingNumber(updatedPo);
      return this._toastr.error(
        notifyMessage.errorMessage.PO_NUMBER_SPACES_NOT_ALLOWED,
        notifyMessage.errorMessage.PO_NUMBER_SPACE_NOT_ALLOWER_TITLE
      );
    }
    this.patchBillingNumber(billingNumber);
  }

  patchBillingNumber(BilingValue: string) {
    this.form.patchValue({ billing_id: BilingValue });
    this.updatePayload({ billing_id: BilingValue });
  }

  private _checkUserIsServiceOperator(): boolean {
    const serviceOperator =
      this.userDetails.operatorAcssLvls === 'admin' ||
      this.userDetails.operatorAcssLvls === 'serviceOperator'
        ? true
        : false;
    return serviceOperator;
  }

  private _checkFeatureAccess(configToCheck: string) {
    const buyerConfig: any = this._auth.buyerConfig;
    const serviceOperator = this._checkUserIsServiceOperator();
    return serviceOperator || !!(buyerConfig && buyerConfig[configToCheck]);
  }

  private _getAllOperators() {
    const operatorUsersSub = this._userService
      .getAllOperatorUser(false)
      .subscribe(
        (response) => {
          this.operatorProjectManagers = response.users;
          this.operatorProjectManagers.splice(0, 0, {
            email: '',
            name: '-- Assign Project manager --',
            company: 0,
            id: 0,
          });
          if (this.surveyDatails?.pm) {
            const selectedPm = this.operatorProjectManagers.filter(
              (user) => user.id === this.surveyDatails.pm?.id
            )[0];
            this.form.patchValue({ pm: selectedPm });
          }
        },
        (error) => {
          this._toastr.error(error.error.msg);
        }
      );
    this._subscriptions.add(operatorUsersSub);
  }

  private _getAllBuyerUser() {
    const buyerUsersSub = this._userService
      .getAllBuyerUser(this.surveyDatails.buyer_company, false)
      .subscribe(
        (response) => {
          this.buyerProjectManagers = response;
          this.buyerProjectManagers.splice(0, 0, {
            email: '',
            name: this.buyerPmLabel,
            company: 0,
            id: 0,
          });
          if (this.surveyDatails?.bpm) {
            const selectedBpm = this.buyerProjectManagers.filter(
              (user) => user.id === this.surveyDatails.bpm?.id
            )[0];
            this.selectBuyerProjectManager(selectedBpm);
          }
        },
        (error) => {
          this._toastr.error(error.error.msg);
        }
      );
    this._subscriptions.add(buyerUsersSub);
  }

  selectProjectManager(pm: ProjectManagers) {
    if (pm?.id === 0) {
      this.form.patchValue({ pm: {} });
      this.updatePayload({ pm: {} });
    } else {
      this.form.patchValue({ pm });
      this.updatePayload({ pm });
    }
  }

  selectBuyerProjectManager(bpm: ProjectManagers) {
    if (bpm?.id === 0) {
      this.form.patchValue({ bpm: {} });
      this.updatePayload({ bpm: {} });
    } else {
      this.form.patchValue({ bpm });
      this.updatePayload({ bpm });
    }
  }

  private _handleSurveyIdChanges() {
    const navigationExtrasImposter$ = this._multiCountryDraftService
      .getMcSpvSelectedChildId()
      .pipe(
        filter((id): id is number => !!id),
        map((id) => <SurveyNavigationState>{ svp_child_id: id })
      );

    const navigationExtras$ = merge(
      this._surveyRouteParamsComposer.extras() as Observable<SurveyNavigationState>,
      navigationExtrasImposter$
    );

    const svpChildSurveyDetails$ = this._surveyIdCommand$.pipe(
      switchMap((routeId) =>
        new SurveyStreamSegregator(
          navigationExtras$,
          routeId
        ).svpChildSurveyIdStream()
      ),
      tap((childId) => (this.surveyId = childId)),
      switchMap((childId) => this._getSurveyDetails(childId, true))
    );

    const notSvpChildSurveyDetails$ = this._surveyIdCommand$.pipe(
      switchMap((routeId) =>
        new SurveyStreamSegregator(
          navigationExtras$,
          routeId
        ).notSvpChildSurveyIdStream()
      ),
      switchMap((surveyId) => this._getSurveyDetails(surveyId)),
      tap((surveyDetail: BuyerSurveyResponse) => {
        if (
          !surveyDetail?.unique_links?.items ||
          !surveyDetail?.unique_links?.items.length
        ) {
          this._clearBuyerEntryLink();
        }
      }),
      share()
    );

    const nonSvpSurveyDetails$ = notSvpChildSurveyDetails$.pipe(
      filter((survey) => !survey.mc || !survey.mc.SVP)
    );

    const svpParentSurvey$ = notSvpChildSurveyDetails$.pipe(
      filter((survey) => !!survey.mc?.SVP && !!survey.mc?.parent)
    );

    const firstSvpChildSurveyDetails$ = svpParentSurvey$.pipe(
      map((survey) => Number(survey.mc.surveys![0].surveyId)),
      tap((childId) => (this.surveyId = childId)),
      switchMap((surveyId) => this._getSurveyDetails(surveyId, true))
    );

    this._subscriptions.add(
      merge(
        nonSvpSurveyDetails$,
        firstSvpChildSurveyDetails$,
        svpChildSurveyDetails$
      )
        .pipe(
          switchMap((survey) => {
            const [languageCode = '', countryCode = ''] =
              survey?.survey_localization?.split('_');
            return this._qualificationService
              .getUIQualifications(countryCode, languageCode)
              .pipe(
                map((qualifications) => {
                  this._aidaPayloadService.setQualifications(qualifications);
                  this._allQualificationsData = qualifications;
                  return survey;
                }),
                catchError(() => {
                  this._aidaPayloadService.setQualifications([]);
                  this._allQualificationsData = [];
                  return of(survey);
                })
              );
          })
        )
        .subscribe((survey) => {
          this._surveyDetailsCommand$.next(survey);
        })
    );
  }

  private _getSurveyDetails(
    surveyId: number,
    accessible?: boolean
  ): Observable<BuyerSurveyResponse> {
    return this._buyerApiService.getSurveyById(surveyId, accessible).pipe(
      catchError((error) => {
        this._toastr.error(error.error.ps_api_response_message);
        return of(null);
      }),
      filter((survey): survey is BuyerSurveyResponse => !!survey)
    );
  }

  private _handleSurveyDetails() {
    const getSurveyDetails$ = this._surveyDetailsCommand$.subscribe(
      async (resp) => {
        if (
          [
            () => resp.ps_survey_status === SURVEY_STATUS.DRAFT,
            () => this.userType === 'operator',
            () => !this.loggedInAsServiceBuyer,
          ].every((item) => item())
        ) {
          this._router.navigateByUrl('/dashboard');
          return;
        }
        this.surveyDatails = resp;
        this.enableSupplierAllocationThrottling =
          this._env.environment.ZAPPI_STORE_BUYER_IDS.includes(
            this.surveyDatails.buyer_company
          ) &&
          !SURVEY_LAUNCH_CONSTANTS.ZAPPI_ALLOCATION_THROTTLING_COUNTRIES.includes(
            this.surveyDatails.locale['country_code']
          )
            ? false
            : this._auth.buyerConfig!.enableSupplierAllocationThrottling;
        if (
          resp.is_decipher_survey &&
          resp.ps_survey_status == SURVEY_STATUS.DRAFT
        ) {
          this.checkAndOpenMissingMarkersModal(resp);
        }
        this.surveyPayload = this._surveyPayloadService.mapSurveyDetails(resp);
        this.qbpSelectionQuotas = [];
        this.isTraditionalMcSurvey = this._isTraditionalMcSurvey();
        this.isMcSvpSurvey = this._isMcSvpSurvey();
        this._sendZipcodePathDetailsToCacheForMcSvpSurvey();
        this.isClickSurvey = !!this.surveyPayload.click_balancing;
        this._surveyMetaDataService.setSurveyData({
          surveyId: resp.ps_survey_id,
          mcUniqueId: resp.mc_unique_id,
          channel_type: resp.channel_type,
          activeTrafficChannel: resp.is_tc_children_active,
        });
        this._setUniqueLinksData(resp.unique_links);
        this.form.patchValue(this.surveyDatails);
        this._createQbpSelectionQuotas(resp.quotas || []);
        const billingId = await this._surveyPO(this.surveyDatails);
        this.form.patchValue({
          completes_required: resp.completes_required,
          uniqBuyerEntryLinkFlag: resp.unique_links.enable,
          subscriber_emails: this._getSubscriberEmails(),
          survey_grouping: this._surveyGrouping(this.surveyDatails),
          billing_id: billingId,
          crmId: resp.crm_id,
          asanaId: resp.asana_id,
          softLaunch: resp.soft_launch.enable,
        });
        this.patchBillingNumber(billingId);
        this.setLiveUrlInfo(resp.live_url || '');
        this.changeTestUrlInfo(resp.test_url || '');
        this.showFeotFields(resp.is_feot_enabled);
        this._pmEmailFieldDisplay(this.surveyDatails.company?.generate_invoice);
        this._setBillingNumberRules();
        if (
          [
            () => this.form.value.uniqBuyerEntryLinkFlag,
            () => resp.unique_links.items.length,
          ].every((item) => item())
        ) {
          this.buyerEntryLinkUploaded = true;
          const testEntryLink = this.generateEntryLink('link');
          this.form.patchValue({
            test_ps_survey_entry_link: testEntryLink,
            live_url: '',
          });
        }

        this._updateScheduleLaunchInformation(this.surveyPayload);
        this._manageLaunchEnableDisable(resp);
        this._setFilterDateRange();
        this.inclExclDisable =
          this.surveyDatails.incl_excl === 1 ? true : false;
        this._setInclExcl(this.surveyDatails.incl_excl);
        if (this.surveyDatails.ps_survey_status == SurveyStatus.Draft) {
          this.manageEdit = true;
        }
        this._getSelectedTemplateDetails(resp.template_id);

        this.form.patchValue({
          hasPSIDRefList: this.surveyDatails.hasPSIDRefList || false,
        });
        this.isSurveySyncedOnNC = !!this.surveyDatails.live_url;
        this.ncSettings = this.surveyDatails?.nc_settings;
        this.form.patchValue({
          softLaunch: this.surveyDatails.soft_launch.enable || false,
          launchquantity: this.surveyDatails.soft_launch.quantity || '',
        });
        this.launchquantityChange();
        this._loadPsidRefList(this.surveyDatails.hasPSIDRefList);
        this._initDone = true;
        if (this._auth.userType === 'operator') {
          this._getAllOperators();
          this._verifyCompanySettings(resp.buyer_company);
        }
        this.surveyPayload.basic_token = this.surveyDatails?.basic_token;
        this.fixedBasicTokenValue = this.surveyDatails?.basic_token?.value;
        this._getAllBuyerUser();
        this.setSubscriberEmails();
        this._updateDataQualityPayload();
        this._locale = {
          languageCode: this.surveyDatails.locale.language_code,
          countryCode: this.surveyDatails.locale.country_code,
        };
      }
    );
    this._subscriptions.add(getSurveyDetails$);
  }

  private _sendZipcodePathDetailsToCacheForMcSvpSurvey() {
    if (!this.isMcSvpSurvey) {
      return;
    }

    const { zipcode_file_path = '', fsacode_file_path = '' } =
      this.surveyDatails;
    this._sendMcSpvDataForUpdate(
      {
        zipcode_file_path,
        fsacode_file_path,
      },
      false
    );
  }

  private _handleFormChangesForUiActivity() {
    this._handleBuyerProjectManagerActivity();
    this._handleSubscriberEmailsActivity();
    this._handleLinkSecurityStatusActivity();
  }

  private _handleBuyerProjectManagerActivity() {
    this._subscriptions.add(
      this.form
        .get('bpm')!
        .valueChanges.pipe(startWith({}), pairwise())
        .subscribe(([previous, current]) => {
          if (previous?.id !== current?.id) {
            const from = previous?.name ? previous.name : '';
            const to = current?.name ? current.name : '';
            this._postActivityLogs('SELECTED_PROJECT_MANAGER', {
              from,
              to,
            });
          }
        })
    );
  }

  private _handleSubscriberEmailsActivity() {
    this._subscriptions.add(
      this.form
        .get('subscriber_emails')!
        .valueChanges.pipe(
          map((emails: string | Array<string>) =>
            this._mapEmails(emails)
              .filter((email) => !!email)
              .join(',')
          ),
          debounceTime(1000),
          pairwise()
        )
        .subscribe(([previous, current]) => {
          if (previous !== current) {
            this._postActivityLogs('LAUNCH_EMAIL_NOTIFICATION', {
              from: previous,
              to: current,
            });
          }
        })
    );
  }

  private _handleLinkSecurityStatusActivity() {
    this._subscriptions.add(
      this.form
        .get('basic_token')!
        .valueChanges.pipe(pairwise())
        .subscribe(([previous, current]) => {
          this._postActivityLogs('LINK_SECURITY_STATUS', {
            from: this.featureStatus(previous.enable),
            to: this.featureStatus(current.enable),
          });
        })
    );
  }

  private _postActivityLogs(
    message: string,
    metadata: { from: string | boolean; to: string | boolean }
  ) {
    if (metadata.from === metadata.to) {
      return;
    }

    this._activityLogService
      .post({
        surveyId: this.surveyId,
        logMessage: message,
        metadata,
      })
      .subscribe();
  }

  private _handleMcSurveyFormChanges() {
    this._handleMcSvpBuyerEntryLinks();
    this._handleMcSurveyBuyerUrlChanges();
    this._handleMcSurveyBillingNumberChanges();
    this._handleMcSvpDqSettingsChanges();
    this._handleMcSvpLaunchSettingsChanges();
    this._handleMcSvpQuotaSettingsChanges();
    this._handleMcSvpFilterAudienceChanges();
    this._handleMcSurveyEmailFeaturesChanges();
    this._handleMcSurveyAutomaticChanges();
  }

  private _handleMcSvpBuyerEntryLinks() {
    const uniqueEntryLinksEnabled$ = this.form.get(
      'uniqBuyerEntryLinkFlag'
    )!.valueChanges;
    const linkItems$ = this._buyerEntryLinksCommand$.pipe(
      map((links) => links.map((link) => ({ link })))
    );
    const testPsEntryLink$ = this.form.get(
      'test_ps_survey_entry_link'
    )!.valueChanges;

    this._subscriptions.add(
      combineLatest([uniqueEntryLinksEnabled$, linkItems$, testPsEntryLink$])
        .pipe(filter(() => this.isMcSvpSurvey))
        .subscribe(
          ([uniqueEntryLinksEnabled, items, test_ps_survey_entry_link]) => {
            const update = {
              unique_links: {
                enable: uniqueEntryLinksEnabled,
                items,
              },
              test_ps_survey_entry_link,
            };
            this._sendMcSpvDataForUpdate(
              update,
              !!this.surveyPayload.is_same_file_entry_links
            );
          }
        )
    );
  }

  private _handleMcSurveyBuyerUrlChanges(): void {
    const liveUrl$ = this.form
      .get('live_url')!
      .valueChanges.pipe(startWith(''));
    const testUrl$ = this.form.get('test_url')!.valueChanges;
    const sameLiveUrlAcrossLocales$ = this.form
      .get('isSameBuyerUrlChecked')!
      .valueChanges.pipe(startWith(false));

    const buyerUrlSettings$ = combineLatest([
      liveUrl$,
      testUrl$,
      sameLiveUrlAcrossLocales$,
    ]).pipe(filter(([liveUrl]) => !!liveUrl));

    const buyerUrlSettingsSvp$ = buyerUrlSettings$.pipe(
      filter(() => this.isMcSvpSurvey)
    );
    const buyerUrlSettingsTraditional$ = buyerUrlSettings$.pipe(
      filter(() => this.isTraditionalMcSurvey)
    );

    this._subscriptions.add(
      buyerUrlSettingsSvp$.subscribe(
        ([liveUrl, testUrl, sameAcrossLocales]) => {
          const update = {
            live_url: liveUrl,
            test_url: testUrl || liveUrl,
          };
          this._sendMcSpvDataForUpdate(update, sameAcrossLocales);
        }
      )
    );

    this._subscriptions.add(
      buyerUrlSettingsTraditional$.subscribe(
        ([liveUrl, testUrl, sameAcrossLocales]) => {
          this.updatePayload({
            live_url: liveUrl,
            test_url: testUrl || liveUrl,
          });
          this._sendMcTraditionalDataForUpdate(
            SURVEY_LAUNCH_CONSTANTS.SURVEY.PATCH_API.PATH.LIVE_URL,
            liveUrl,
            sameAcrossLocales
          );
          this._sendMcTraditionalDataForUpdate(
            SURVEY_LAUNCH_CONSTANTS.SURVEY.PATCH_API.PATH.TEST_URL,
            testUrl || liveUrl,
            sameAcrossLocales
          );
        }
      )
    );
  }

  private _handleMcSurveyBillingNumberChanges(): void {
    const billingId$ = this.form
      .get('billing_id')!
      .valueChanges.pipe(startWith(''));
    const sameBillingAcrossLocales$ = this.form
      .get('isSameBillingSurveyPoChecked')!
      .valueChanges.pipe(startWith(false));

    const billingSettingSvp$ = combineLatest([
      billingId$.pipe(filter(() => this.isMcSvpSurvey)),
      sameBillingAcrossLocales$,
    ]);
    const billingSettingTraditional$ = combineLatest([
      billingId$,
      sameBillingAcrossLocales$,
    ]).pipe(filter(() => this.isTraditionalMcSurvey));

    this._subscriptions.add(
      billingSettingSvp$.subscribe(([surveyPo, sameAcrossLocales]) => {
        const update = {
          billing_id: surveyPo,
        };
        this._sendMcSpvDataForUpdate(update, sameAcrossLocales);
      })
    );

    this._subscriptions.add(
      billingSettingTraditional$.subscribe(([billingId, sameAcrossLocales]) => {
        this._sendMcTraditionalDataForUpdate(
          SURVEY_LAUNCH_CONSTANTS.SURVEY.PATCH_API.PATH.BILLING_ID,
          billingId,
          sameAcrossLocales
        );
      })
    );
  }

  private _handleMcSvpDqSettingsChanges(): void {
    const dqSettings$ = this.form.get('dq_settings')!.valueChanges;
    const dqEnabled$ = this.form.get('is_dq_enable')!.valueChanges;
    const sameDqAcrossLocales$ = this.form.get(
      'isSameDqSettingsChecked'
    )!.valueChanges;

    this._subscriptions.add(
      combineLatest([dqSettings$, dqEnabled$, sameDqAcrossLocales$])
        .pipe(filter(() => this.isMcSvpSurvey))
        .subscribe(([dqSettings, isDqEnabled, sameAcrossLocales]) => {
          const update = {
            dq_settings: dqSettings,
            is_dq_enable: isDqEnabled,
          };
          this._sendMcSpvDataForUpdate(update, sameAcrossLocales);
        })
    );
  }

  private _handleMcSvpLaunchSettingsChanges(): void {
    const basicToken$ = this.form
      .get('basic_token')!
      .valueChanges.pipe(map((value) => ({ basic_token: value })));
    const scheduleLaunch$ = combineLatest([
      this.form.get('schedule_launch')!.valueChanges,
      this.form.get('scheduledLaunchDetails')!.valueChanges,
    ]).pipe(
      map((values) => ({
        schedule_launch: this._mapScheduleLaunchPayload(...values),
      }))
    );
    const softLaunch$ = combineLatest([
      this.form.get('softLaunch')!.valueChanges,
      this.form.get('launchquantity')!.valueChanges,
    ]).pipe(map((values) => this._mapSoftLaunchPayload(...values)));
    const sameLaunchSettingsAcrossLocales$ = this.form.get(
      'isSameLaunchSettingsChecked'
    )!.valueChanges;

    this._subscriptions.add(
      combineLatest([
        basicToken$,
        scheduleLaunch$,
        softLaunch$,
        sameLaunchSettingsAcrossLocales$,
      ])
        .pipe(filter(() => this.isMcSvpSurvey))
        .subscribe(
          ([basicToken, scheduleLaunch, softLaunch, sameAcrossLocales]) => {
            const update = <Omit<BuyerSurveyResponse, 'survey_localization'>>{
              ...basicToken,
              ...scheduleLaunch,
              ...softLaunch,
            };
            this._sendMcSpvDataForUpdate(update, sameAcrossLocales);
          }
        )
    );
  }

  private _handleMcSvpQuotaSettingsChanges(): void {
    const quotaThrottling$ = this.form.get('quota_throttling')!.valueChanges;
    const feotEnabled$ = this.form.get('is_feot_enabled')!.valueChanges;
    const feot$ = this.form.get('feot')!.valueChanges;
    const sameQuotaAcrossLocales$ = this.form.get(
      'isSameQuotaSettingsChecked'
    )!.valueChanges;
    const supplierAllocationThrottling$ = this.form.get(
      'supplier_allocation_throttle'
    )!.valueChanges;

    this._subscriptions.add(
      combineLatest([
        quotaThrottling$,
        feotEnabled$,
        feot$,
        sameQuotaAcrossLocales$,
        supplierAllocationThrottling$,
      ])
        .pipe(filter(() => this.isMcSvpSurvey))
        .subscribe(
          ([
            quotaThrottlingEnabled,
            feotEnabled,
            feot,
            sameAcrossLocales,
            supplierAllocationThrottling,
          ]) => {
            const update = {
              quota_throttling: quotaThrottlingEnabled,
              feot,
              is_feot_enabled: feotEnabled,
              supplier_allocation_throttle: supplierAllocationThrottling,
            };
            this._sendMcSpvDataForUpdate(update, sameAcrossLocales);
          }
        )
    );
  }

  private _handleMcSvpFilterAudienceChanges(): void {
    const includeExclude$ = this.form.get('incl_excl')!.valueChanges;
    const surveyGrouping$ = this.form
      .get('survey_grouping')!
      .valueChanges.pipe(map((value) => this._mapSurveyGroupingPayload(value)));
    const psIds$ = this._psIdsCommand$.pipe(
      map((psIds) => this._mapPsIdsPaylod(psIds)),
      startWith([])
    );
    const surveyExternalId$ = this.form.get('survey_external_id')!.valueChanges;
    const sameFilterAudienceAcrossLocales$ = this.form.get(
      'isSameFilterAudienceChecked'
    )!.valueChanges;

    this._subscriptions.add(
      combineLatest([
        includeExclude$,
        surveyGrouping$,
        psIds$,
        surveyExternalId$,
        sameFilterAudienceAcrossLocales$,
      ])
        .pipe(filter(() => this.isMcSvpSurvey))
        .subscribe(
          ([
            incl_excl,
            survey_grouping,
            ref_list,
            survey_external_id,
            sameAcrossLocales,
          ]) => {
            const update = {
              incl_excl,
              survey_grouping,
              ref_list,
              survey_external_id,
            };
            this._sendMcSpvDataForUpdate(update, sameAcrossLocales);
          }
        )
    );
  }

  private _handleMcSurveyEmailFeaturesChanges(): void {
    const subscriberEmails$ = this.form
      .get('subscriber_emails')!
      .valueChanges.pipe(
        map((emails) => this._mapEmails(emails)),
        startWith([])
      );
    const pmEmails$ = this.form
      .get('pm_emails')!
      .valueChanges.pipe(
        map((emails) => this._mapEmails(emails), startWith([]))
      );
    const buyerMessage$ = this.form
      .get('buyer_message')!
      .valueChanges.pipe(startWith(''));
    const sameEmailFeaturesAcrossLocales$ = this.form
      .get('isSameEmailFeaturesChecked')!
      .valueChanges.pipe(startWith(false));

    const emailSettings$ = combineLatest([
      subscriberEmails$,
      pmEmails$,
      buyerMessage$,
      sameEmailFeaturesAcrossLocales$,
    ]);

    const emailSettingsSvp$ = emailSettings$.pipe(
      filter(() => this.isMcSvpSurvey)
    );
    const emailSettingsTraditional$ = emailSettings$.pipe(
      filter(() => this.isTraditionalMcSurvey)
    );

    this._subscriptions.add(
      emailSettingsSvp$.subscribe(
        ([subscriber_emails, pm_emails, buyer_message, sameAcrossLocales]) => {
          const update = {
            subscriber_emails,
            pm_emails,
            buyer_message: buyer_message || '',
          };
          this._sendMcSpvDataForUpdate(update, sameAcrossLocales);
        }
      )
    );

    this._subscriptions.add(
      emailSettingsTraditional$.subscribe(
        ([subscriber_emails, pm_emails, buyer_message, sameAcrossLocales]) => {
          this._sendMcTraditionalDataForUpdate(
            SURVEY_LAUNCH_CONSTANTS.SURVEY.PATCH_API.PATH.SUBSCRIBER_EMAILS,
            subscriber_emails,
            sameAcrossLocales
          );
          this._sendMcTraditionalDataForUpdate(
            SURVEY_LAUNCH_CONSTANTS.SURVEY.PATCH_API.PATH.PM_EMAILS,
            pm_emails,
            sameAcrossLocales
          );
          this._sendMcTraditionalDataForUpdate(
            SURVEY_LAUNCH_CONSTANTS.SURVEY.PATCH_API.PATH.BUYER_MESSAGE,
            buyer_message,
            sameAcrossLocales
          );
        }
      )
    );
  }

  private _handleMcSurveyAutomaticChanges(): void {
    const bpm$ = this.form.get('bpm')!.valueChanges.pipe(startWith({}));
    const opm$ = this.form.get('pm')!.valueChanges.pipe(startWith({}));
    const crmId$ = this.form.get('crmId')!.valueChanges.pipe(startWith(''));
    const asanaId$ = this.form.get('asanaId')!.valueChanges.pipe(startWith(''));

    const automaticSettings$ = combineLatest([bpm$, opm$, crmId$, asanaId$]);
    const automaticSettingsMcSvp$ = automaticSettings$.pipe(
      filter(() => this.isMcSvpSurvey)
    );
    const automaticSettingsMcTraditional$ = automaticSettings$.pipe(
      filter(() => this.isTraditionalMcSurvey)
    );

    this._subscriptions.add(
      automaticSettingsMcSvp$.subscribe(([bpm, pm, crmId, asanaId]) => {
        const update = {
          bpm,
          ...(this._auth.userType === 'operator' && {
            pm,
            crm_id: crmId,
            asana_id: asanaId,
          }),
        };
        this._sendMcSpvDataForUpdate(update, true);
      })
    );

    this._subscriptions.add(
      automaticSettingsMcTraditional$.subscribe(([bpm, pm, crmId, asanaId]) => {
        this._sendMcTraditionalProjectManagersDataForUpdate(
          SURVEY_LAUNCH_CONSTANTS.SURVEY.PATCH_API.PATH.BUYER_PROJECT_MANAGER,
          bpm,
          true
        );
        if (this._auth.userType === 'operator') {
          this._sendMcTraditionalProjectManagersDataForUpdate(
            SURVEY_LAUNCH_CONSTANTS.SURVEY.PATCH_API.PATH
              .OPERATOR_PROJECT_MANAGER,
            pm,
            true
          );
          this._sendMcTraditionalDataForUpdate(
            SURVEY_LAUNCH_CONSTANTS.SURVEY.PATCH_API.PATH.CRM_ID,
            crmId,
            true
          );
          this._sendMcTraditionalDataForUpdate(
            SURVEY_LAUNCH_CONSTANTS.SURVEY.PATCH_API.PATH.ASANA_ID,
            asanaId,
            true
          );
        }
      })
    );
  }

  private _sendMcTraditionalProjectManagersDataForUpdate(
    path: string,
    value: ProjectManagers,
    propogateToAll = false
  ) {
    if (!value || !value.id) {
      return;
    }
    this._sendMcTraditionalDataForUpdate(path, value.id, propogateToAll);
  }

  private _sendMcTraditionalDataForUpdate(
    path: string,
    value: PatchSurveyInterface['value'],
    propogateToAll = false
  ) {
    if (!value) {
      return;
    }
    if (propogateToAll) {
      const updates = this._multicountrySurveys.map(({ survey_id }) => {
        return <McTraditionalSetterData>{
          survey_id,
          path,
          value,
          operation: 'replace',
        };
      });

      this._multiCountryDraftService.sendMcTraditionalUpdateInCache(updates);
      return;
    }

    const update: McTraditionalSetterData = {
      survey_id: Number(this.surveyDatails.ps_survey_id),
      path,
      value,
      operation: 'replace',
    };
    this._multiCountryDraftService.sendMcTraditionalUpdateInCache([update]);
  }

  private _sendMcSpvDataForUpdate(
    update: Partial<Omit<BuyerSurveyResponse, 'survey_localization'>>,
    propoageToAll = false
  ) {
    if (propoageToAll) {
      const cache = {
        for: 'all' as const,
        data: update,
      };
      this._multiCountryDraftService.sendMcSpvUpdateInCacheCommand(cache);
      return;
    }

    const eachUpdate = {
      survey_localization: this.surveyDatails.survey_localization,
      ...update,
    };
    const cache = {
      for: 'each' as const,
      data: [eachUpdate],
    };
    this._multiCountryDraftService.sendMcSpvUpdateInCacheCommand(cache);
  }

  private _isTraditionalMcSurvey(): boolean {
    return !!this.surveyDatails.mc_unique_id && !this.surveyDatails.mc?.SVP;
  }

  private _isMcSvpSurvey(): boolean {
    return !!this.surveyDatails.mc?.SVP;
  }

  private _getSelectedTemplateDetails(templateId?: number) {
    if (!templateId) {
      return;
    }
    this._templateService.getById(templateId).subscribe((resp) => {
      this.selectedTemplateResponse = resp;
    });
  }

  private _loadPsidRefList(hasPSIDRefList: boolean | undefined) {
    if (!hasPSIDRefList) {
      return;
    }
    this._buyerApiService
      .getBuyerUploadedList(
        this.surveyDatails.ps_survey_id,
        this.surveyDatails.incl_excl
      )
      .subscribe((psidsResp: PsidsBuyerUploadListResponse) => {
        if (psidsResp && psidsResp.psids && psidsResp.psids.length) {
          let surveyIds = this._getSurveysIdsFromForm().filter(
            (surveyId) =>
              Number(this.surveyDatails.ps_survey_id) !== Number(surveyId)
          );
          if (this.isMcSvpSurvey) {
            // remove parent id from list
            surveyIds = surveyIds.filter(
              (surveyId) =>
                Number(this.surveyDatails.ps_project_id) !== Number(surveyId)
            );
          }
          /**
           * INCLUDE case - When PSIDs are available, but survey grouping (excluding self survey) is not there, then
           * it's manual upload case otherwise automatic upload.
           * EXCLUDE case - When PSIDs are avaialable, it automatically means it was manually uploaded because we do
           * not allow PSID upload through date range in exclude case.
           */
          this.inclExclBuyerUpload = !this.inclExclDisable || !surveyIds.length;
          this.surveyGroupingPSIDUpload = !!(
            this.inclExclDisable && surveyIds?.length
          );
          this._psids = psidsResp.psids;
          this._psIdsCommand$.next(this._psids);
          this._includePsids = psidsResp.psids.map((psid) => psid.PSID);
          this._updateSurveyData();
        }
      });
  }

  private _setUniqueLinksData(uniqueLinkData: UniqueLinks) {
    if (uniqueLinkData.enable) {
      this.setBuyerLinkSliderValue();
      if (uniqueLinkData.items && uniqueLinkData.items.length) {
        const mappedLinks = uniqueLinkData.items
          .map((item) => (item && item.link ? item.link : ''))
          .filter(Boolean);
        this.buyerEntryLinksArr = [...new Set(mappedLinks)];
        this._buyerEntryLinksCommand$.next(mappedLinks);
      }
    }
  }

  public setBuyerLinkSliderValue() {
    if (this.surveyDatails.ps_survey_status !== SURVEY_STATUS.DRAFT) {
      this.isBuyerLinkSliderDisabled = true;
    } else {
      this.isBuyerLinkSliderDisabled = false;
    }
  }

  private _generateForm() {
    this.form = this._fb.group({
      uniqBuyerEntryLinkFlag: [false],
      schedule_launch: this._fb.group({
        enable: [true],
        info: [],
      }),
      basic_token: this._fb.group({
        enable: [false],
        value: [''],
      }),
      scheduledLaunchDetails: this._fb.group({
        date: [],
        minutes: [],
        hours: [],
        timezone: [],
      }),
      qbp: this._fb.group({
        enable: [false],
        quota_ids: [],
        selectedQuotaIds: [],
        category: [],
      }),
      aida: this._fb.group({
        enabled: [false],
        selected_quota_ids: [[]],
      }),
      qlc: this._fb.group({
        enable: [false],
        multiple_quotas: this._fb.group({
          enable: false,
          count_by_quota: false,
          compensate_by_quota: false,
        }),
      }),
      is_aida_enabled: [false],
      aidaSelectedQuota: [],
      qbpSelectedQuota: [],
      softLaunch: [false],
      quota_throttling: [false],
      launchquantity: [],
      launchpercentile: [],
      is_feot_enabled: [false],
      is_modular_survey: [false],
      survey_external_id: [''],
      completes_required: [],
      field_time: [],
      is_qbp_enable: [false],
      buyerMsg: [],
      subscriber_emails: [],
      live_url: ['', [Validators.required]],
      test_url: [],
      billing_id: [],
      url_transform: [false],
      testUrlTT: [],
      liveUrlTT: [],
      liveWarningMsg: [],
      testWarningMsg: [],
      nonHttpsWarningMsg: [],
      survey_client: [],
      test_ps_survey_entry_link: [],
      survey_grouping: [],
      buyer_message: [],
      pm_emails: [],
      incl_excl: 0,
      hasPSIDRefList: false,
      isSameBuyerUrlChecked: [false],
      isSameFileEntryLinksChecked: [false],
      isSameBillingSurveyPoChecked: [false],
      isSameDqSettingsChecked: [false],
      isSameLaunchSettingsChecked: [false],
      isSameQuotaSettingsChecked: [false],
      isSameFilterAudienceChecked: [false],
      isSameEmailFeaturesChecked: [false],
      crmId: [],
      pm: [],
      bpm: [],
      asanaId: [],
      is_dq_enable: !!this._auth.buyerConfig?.enableProjectLevelDataQuality,
      is_enable_margin_max: [false],
      supplier_allocation_throttle: [false],
      feot: this._fb.group({
        pacing: ['days'],
        interval: [],
        is_custom: [],
      }),
      dq_settings: this._fb.group({
        purescore_filter_enabled:
          !!this._auth.buyerConfig?.enableDefaultSettingsPureScore,
        fp_device_check: !!this._auth.buyerConfig?.enableSettingsFpFraudCheck,
        fp_fraud_check: !!this._auth.buyerConfig?.enableSettingsFpFraudCheck,
      }),
    });
  }

  private _pmEmailFieldDisplay(generateInvoice: string) {
    if (['PO', 'PROJECT'].includes(generateInvoice)) {
      this.pmEmailFieldDisplay = true;
    }
  }

  private async _surveyPO(surveyDatails: BuyerSurveyResponse) {
    if (
      this.surveyDatails.ps_survey_status === SURVEY_STATUS.DRAFT &&
      this.surveyDatails.is_clone_survey &&
      !this.surveyDatails.is_clone_billing_id_changed
    ) {
      const modalRef = this._modal.open(ConfirmationModalComponent, {
        data: {
          text: 'Would you like to use the same PO Number as the original survey? Doing so will combine the data with other surveys that share the same PO Number. Click "No" to enter a new PO Number.',
          cancelButtonLabel: 'Yes',
          confirmButtonLabel: 'No',
        },
        width: '450px',
      });
      const response = await modalRef.onClose$
        .pipe(map((msg) => msg === 'ok'))
        .toPromise();
      if (response) {
        return '';
      }
    }

    const defaultPo = this.billingRules
      ? this.billingRules.example
      : `PS${this.surveyId}`;

    const po = surveyDatails.billing_id ? surveyDatails.billing_id : defaultPo;
    return po;
  }

  private _surveyGrouping(surveyDatails: BuyerSurveyResponse) {
    let surveyGrouping = surveyDatails.survey_grouping;
    surveyGrouping.push(surveyDatails.ps_survey_id);
    this.surveyGroupingBeforeUIChange = [
      ...new Set(surveyGrouping.map((elem) => elem)),
    ];
    return this.surveyGroupingBeforeUIChange;
  }

  public checkWarnGroupingRemoval() {
    const pristineGrouping = this.surveyGroupingBeforeUIChange;
    const newGrouping = this.form.value.survey_grouping;
    if (pristineGrouping.some((elem) => !newGrouping.includes(elem))) {
      if (!this.enableFilteringByDate) {
        this._toastr.warning(
          notifyMessage.warningMessage.LAUNCH_SURVEY
            .SURVEY_REMOVE_FROM_PRISTINEGROUPING
        );
      }
    }
  }

  private _getSubscriberEmails() {
    return this.surveyDatails.subscriber_emails &&
      this.surveyDatails.subscriber_emails[0]
      ? this.surveyDatails.subscriber_emails
      : this.userDetails.eml;
  }

  public setEntryLinkFlag() {
    const upcomingFlag = !this.form.value.uniqBuyerEntryLinkFlag;
    let testEntryLink = '';
    if (!upcomingFlag) {
      testEntryLink = this.generateEntryLink(this.form.value.live_url || '');
    } else if (this.buyerEntryLinksArr.length) {
      this.buyerEntryLinkUploaded = true;
      testEntryLink = this.generateEntryLink('link');
    }

    if (upcomingFlag) {
      this.form.patchValue({ live_url: '' });
      this.updatePayload({ live_url: '' });
      this.form.patchValue({
        isSameFileEntryLinksChecked: false,
      });
    }

    this.form.patchValue({
      uniqBuyerEntryLinkFlag: upcomingFlag,
      test_ps_survey_entry_link: testEntryLink,
    });
    this.updatePayload({
      unique_links: {
        enable: this.form.value.uniqBuyerEntryLinkFlag,
        items: this.buyerEntryLinksArr,
      },
      test_ps_survey_entry_link: testEntryLink,
    });

    this.warningFlg = false;

    if (!upcomingFlag) {
      this._surveyMetaDataService.saveSurvey();
    }
  }

  public onSameUrlCheckChange() {
    this.form.patchValue({
      isSameBuyerUrlChecked: !this.form.value.isSameBuyerUrlChecked,
    });
    this.updatePayload({
      is_same_buyer_url_checked: this.form.value.isSameBuyerUrlChecked,
    });
  }

  public onBillingCheckChange() {
    if (this.form.value.billing_id?.length) {
      this.form.patchValue({
        isSameBillingSurveyPoChecked:
          !this.form.value.isSameBillingSurveyPoChecked,
      });
    }
  }

  private _patchSurvey(surveyId: number, payload: PatchSurveyInterface[]) {
    return this._buyerApiService
      .patchSurvey(surveyId, payload)
      .pipe(
        catchError((error) =>
          throwError(new SurveyUpdateError(error, this._auth.buyerConfig!))
        )
      )
      .toPromise()
      .catch((error) => this._toastr.error(error.message));
  }

  public updateScheduleaunchFlag(scheduleLaunchChange: boolean = false) {
    this.form.patchValue({
      schedule_launch: {
        enable: scheduleLaunchChange,
      },
    });

    this.softLaunchDisable = scheduleLaunchChange;
    this.softLaunchSlideDisable = scheduleLaunchChange;
    this.fieldOverTimeDisable = scheduleLaunchChange;
    this.updatePayload({
      schedule_launch: {
        enable: scheduleLaunchChange,
      },
    });
  }

  public updateLinkSecurity(linkSecurityChange: boolean = true) {
    this.notEditableBasicToken = true;
    const patchPayload = [
      {
        op: 'replace',
        path: '/basic_token',
        value: {
          enable: linkSecurityChange,
          value: this.form.value.basic_token.value,
        },
      },
    ];
    this._buyerApiService
      .patchSurvey(this.surveyId, patchPayload)
      .toPromise()
      .then(
        (response) => {
          this._toastr.success(notifyMessage.successMessage.BASIC_TOKEN_SAVED);
          const basicTokenValue = response.basic_token.value
            ? response.basic_token.value
            : '';
          this.form.patchValue({
            basic_token: {
              enable: response.basic_token.enable,
              value: basicTokenValue,
            },
          });
          this.fixedBasicTokenValue = basicTokenValue;
          this.updatePayload({
            basic_token: response.basic_token,
          });
        },
        (error) => {
          this._toastr.error(error.error.ps_api_response_message);
          if (this.form.value.basic_token.value.length > 0) {
            this.form.patchValue({
              basic_token: {
                enable: true,
                value: this.fixedBasicTokenValue,
              },
            });
          }
        }
      );
  }

  public downloadSampleUniqBuyerEntryLink() {
    window.open(this.buyerEntryLinkInfo[0].templatePath, '_blank');
  }

  public async uploadMappings(event: Event) {
    const target = event.target as HTMLInputElement;
    const file: File = (target.files as FileList)[0];
    const fileExtension = file.name.split('.').pop();
    if (fileExtension === 'csv') {
      const csvDataInJson: any = await this._csvToJson.csvToJson(file);
      this.buyerEntryLinksArr = csvDataInJson.map((i: Array<string>) => i[0]);
    } else if (fileExtension === 'xls' || fileExtension === 'xlsx') {
      this.buyerEntryLinksArr = await this._xlsxToJson.xlsxToJson(file);
    } else {
      throw new Error('Invalid file extension.');
    }
    // Removing the null or empty or duplicate URLs
    this.buyerEntryLinksArr = [
      ...new Set(this.buyerEntryLinksArr.filter(Boolean)),
    ];
    this._buyerEntryLinksCommand$.next(this.buyerEntryLinksArr);
    this.setBuyerLinkSliderValue();
    this.buyerEntryLinkUploaded = true;
    const testEntryLink = this.generateEntryLink('link');
    this.form.patchValue({
      test_ps_survey_entry_link: testEntryLink,
    });
    this.updatePayload({
      unique_links: {
        enable: this.form.value.uniqBuyerEntryLinkFlag,
        items: this.buyerEntryLinksArr,
      },
      is_same_file_entry_links: !!this.form.value.isSameFileEntryLinksChecked,
      test_ps_survey_entry_link: testEntryLink,
    });
    if (this._isMcSvpSurvey()) {
      this._updateUniqueBuyerEntryLinkMcSPVPayLoad();
    }
    this._surveyMetaDataService.saveSurvey();
  }

  public clearUploadedBuyerEntryLinks() {
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      width: '373px',
      data: {
        text: 'Are you sure you want to clear Buyer Entry Links?',
        confirmButtonLabel: 'Clear Buyer Entry Links',
      },
    });

    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'ok') {
        this.buyerEntryLinksArr = [];
        this._buyerEntryLinksCommand$.next([]);
        this.buyerEntryLinkUploaded = false;
        const testEntryLink = '';
        const { isSameFileEntryLinksChecked, uniqBuyerEntryLinkFlag } =
          this.form.value;
        this.form.patchValue({
          test_ps_survey_entry_link: testEntryLink,
        });
        this.isBuyerLinkSliderDisabled = false;
        const uniqueLinksEnable = isSameFileEntryLinksChecked
          ? false
          : uniqBuyerEntryLinkFlag;
        this.updatePayload({
          unique_links: {
            enable: uniqueLinksEnable,
            items: this.buyerEntryLinksArr,
          },
          test_ps_survey_entry_link: testEntryLink,
        });
        if (isSameFileEntryLinksChecked) {
          this._clearUniqueEntryLinksMcSurveys();
        }
      }
    });
  }

  public viewBuyerLinks() {
    this._modal.open(BuyerEntryLinkViewModalComponent, {
      data: this.buyerEntryLinksArr,
      width: '80%',
      height: '80%',
    });
  }

  public launchquantityChange() {
    this.form.controls.launchpercentile.setValue(
      Math.round(
        (this.form.value.launchquantity * 100) /
          this.form.value.completes_required
      )
    );

    this.updatePayload({
      soft_launch: {
        enable: this.form.value.softLaunch,
        quantity: this.form.value.launchquantity,
      },
    });
  }

  public launchpercentileChange() {
    this.form.controls.launchquantity.setValue(
      Math.round(
        (this.form.value.launchpercentile *
          this.form.value.completes_required) /
          100
      )
    );

    this.updatePayload({
      soft_launch: {
        enable: this.form.value.softLaunch,
        quantity: this.form.value.launchquantity,
      },
    });
  }

  toggleModularSurvey(isEnabled: boolean) {
    this.form.controls?.is_modular_survey?.setValue(isEnabled);
    this.updatePayload({ is_modular_survey: isEnabled });
  }

  toggleSupAllocationThrottle(isEnabled: boolean) {
    this.form.controls?.supplier_allocation_throttle?.setValue(isEnabled);
    this.updatePayload({ supplier_allocation_throttle: isEnabled });
  }

  public updateSoftLaunch(softLaunchChange: boolean = false) {
    this.scheduleLaunchSlideDisable =
      this.surveyDatails.ps_survey_status !== SurveyStatus.Draft ||
      softLaunchChange;
    this.fieldOverTimeDisable = softLaunchChange;
    if (this.form.value.is_feot_enabled && softLaunchChange) {
      this._toastr.error(notifyMessage.errorMessage.SOFT_LAUNCH_ERROR_MESSAGE);
      this.form.controls.is_feot_enabled.setValue(false);
    }
    this.form.controls.softLaunch.setValue(!this.form.value.softLaunch);
    this.updatePayload({
      soft_launch: {
        enable: this.form.value.softLaunch,
        quantity: this.form.value.launchquantity,
      },
    });
  }

  public updateQuotaThrottling() {
    this.form.controls.quota_throttling.setValue(
      !this.form.value.quota_throttling
    );

    this.updatePayload({
      quota_throttling: this.form.value.quota_throttling,
    });
  }

  public showFeotFields(feotchange: boolean = false) {
    this.form.controls.is_feot_enabled.setValue(feotchange);
    this.scheduleLaunchSlideDisable =
      this.surveyDatails.ps_survey_status !== SurveyStatus.Draft || feotchange;
    this.softLaunchDisable = feotchange;
    this.softLaunchSlideDisable = feotchange;
    if (this.form.value.softLaunch && feotchange) {
      this._toastr.error(notifyMessage.errorMessage.SOFT_LAUNCH_ERROR_MESSAGE);
      this.form.controls.softLaunch.setValue(false);
    }
    this._calculateFeotFields();
    this.updatePayload({ is_feot_enabled: feotchange });
  }

  private _calculateFeotFields() {
    if (this.form.value.is_feot_enabled) {
      const totalClicksOrCompletes = this.isClickSurvey
        ? this.surveyPayload.estimated_clicks
        : this.form.value.completes_required;
      const remainingTime = this._calculateFeotRemainingTime();

      this.feotFields.feotQuantity =
        remainingTime <= 0
          ? Math.round(totalClicksOrCompletes / this.surveyPayload.field_time)
          : Math.round(totalClicksOrCompletes / remainingTime);
      this.feotFields.feotRemainingTime =
        remainingTime <= 0 ? 0 : remainingTime;
    } else {
      this.feotFields.feotQuantity = 200;
      this.feotFields.feotRemainingTime = 5;
    }
    this._eventEmitterService.sendFeotFieldCalculation(this.feotFields);
  }

  private _calculateFeotRemainingTime() {
    const { field_time: fieldTimeDays } = this.form.value;
    const { pacing, is_custom: isCustomPacing } = this.form.value.feot;
    let scopedPacing = isCustomPacing ? pacing : 'days';

    const fieldTimeWeeks = fieldTimeDays / 7;
    const fieldTimeHours = fieldTimeDays * 24;

    if (this.surveyDatails.ps_survey_status !== SURVEY_STATUS.LIVE) {
      switch (scopedPacing) {
        case 'weeks':
          return Math.ceil(fieldTimeWeeks);
        case 'days':
          return Math.ceil(fieldTimeDays);
        case 'hours':
          return Math.ceil(fieldTimeHours);
        default:
          return Math.ceil(fieldTimeDays);
      }
    }

    const timeElapsed = Date.now() - this.surveyDatails.launch_date;
    const timeElapsedHours = timeElapsed / 1000 / 60 / 60;
    const timeElapsedDays = timeElapsedHours / 24;
    const timeElapsedWeeks = timeElapsedDays / 7;
    console.log(
      'time elapsed h:',
      timeElapsedHours,
      'fieldtime h:',
      fieldTimeHours
    );

    switch (scopedPacing) {
      case 'weeks':
        return Math.ceil(fieldTimeWeeks - timeElapsedWeeks);
      case 'days':
        return Math.ceil(fieldTimeDays - timeElapsedDays);
      case 'hours':
        return Math.ceil(fieldTimeHours - timeElapsedHours);
      default:
        return Math.ceil(fieldTimeDays - timeElapsedDays);
    }
  }

  public openTab(url: string) {
    if (this.form.value.uniqBuyerEntryLinkFlag) {
      // If user attempts to test URL, but buyer entry links not uploaded
      if (!this.buyerEntryLinkUploaded) {
        return this._toastr.error(
          notifyMessage.errorMessage.ALL_FIELDS_REMAINING
        );
      }
    }
    if (!url) {
      return this._toastr.error(notifyMessage.errorMessage.URL_UPDATE);
    }
    // Testing URL in case of Unique buyer entry links - No patching of live URL required
    if (this.form.value.uniqBuyerEntryLinkFlag) {
      return window.open(url, '_blank');
    }
    // Testing URL in case of Live URL
    const payload = [
      {
        op: 'replace',
        path: `/live_url`,
        value: this.form.value.live_url,
      },
      {
        op: 'replace',
        path: `/test_url`,
        value:
          this.form.value.test_url !== '' && this.form.value.test_url !== null
            ? this.form.value.test_url
            : this.form.value.live_url,
      },
    ];
    this._patchSurvey(this.surveyId, payload).then(
      (response) => {
        window.open(url, '_blank');
      },
      (error) => {
        this._toastr.error(error.error.ps_api_response_message);
      }
    );
  }

  public setLiveUrlInfo(url: string) {
    const spaceValidator = new SpaceValidator(url);
    if (spaceValidator.check()) {
      url = spaceValidator.remove();
      this.form.patchValue({
        live_url: url,
      });
      this._toastr.error(notifyMessage.errorMessage.URL_SPACE_NOT_ALLOWED);
    }

    if (
      !url &&
      this.finalizeInitialValues.live_url &&
      !this.form.get('live_url')?.dirty
    ) {
      url = this.finalizeInitialValues.live_url;
      this.form.patchValue(
        { live_url: this.finalizeInitialValues.live_url },
        { emitEvent: false }
      );
    }

    this._changeLiveUrlInfo(url);
    const testEntryLink = this.generateEntryLink(url);
    this.form.patchValue({
      test_ps_survey_entry_link: testEntryLink,
    });
    this.updatePayload({
      live_url:
        this.surveyPayload.unique_links &&
        this.surveyPayload.unique_links.enable
          ? ''
          : url,
      test_ps_survey_entry_link: testEntryLink,
    });
    this._setMultiCountrySurveyData();
  }

  public generateEntryLink(url: string) {
    const fieldTime = this.form.value.field_time;
    let testLink = '';

    const surveyId = !!this._mcSpvParentSurvey
      ? this._mcSpvParentSurvey.ps_survey_id
      : this.surveyId;
    const localization = this.surveyDatails.survey_localization;
    if (url && fieldTime) {
      testLink = `${this._baseUrl}/#/start-survey?survey_id=${surveyId}&ps_redirect_test=1&bsec=${GlobalConstants.BSEC}&supplier_id=${this._env.environment.BUYER_TEST_LINK_SUPPLIER_ID}`;
    }
    if (localization && !!this._mcSpvParentSurvey) {
      testLink += `&localization=${localization}`;
    }
    return testLink;
  }

  /**
   * @param {isURLTransform} : Flag to check variable mapping is disable or Not
   *  check live and test url variable mapping is disable or Not
   * */
  public checkDisableAutoMapping(disableUrlMapping: boolean) {
    if (disableUrlMapping) {
      this.warningTestFlg = false;
      this.warningFlg = false;
      this.testUrlCheck = false;
      this.liveUrlCheck = false;
      this.form.patchValue({
        liveUrlTT: this.form.value.live_url
          ? `Since you have disabled automatic variable mapping, no variables will be appended to your link.  Respondents will be redirected to: ${this.form.value.live_url}`
          : `Please enter your Survey URL to check its final form.`,
      });
      this.form.patchValue({
        testUrlTT: this.form.value.test_url
          ? `Since you have disabled automatic variable mapping, no variables will be appended to your link.  Respondents will be redirected to: ${this.form.value.test_url}`
          : `Your TEST URL is empty, we will use your LIVE URL as the TEST URL. Please add a TEST URL if it is different than the LIVE URL.`,
      });
    } else {
      this._getBuyerInfo(this.userDetails.cmp);
      this._changeLiveUrlInfo(this.form.value.live_url);
      this.changeTestUrlInfo(this.form.value.test_url);
    }

    this.updatePayload({ url_transform: disableUrlMapping });
  }

  private _getBuyerInfo(id: number) {
    const buyerSetting$ = this._buyerSettingsService
      .getBuyerSettings(id)
      .subscribe(
        (res) => {
          if (res.buyer.variable_mapping) {
            for (let i = 0; i < res.buyer.variable_mapping.length; i++) {
              for (let j in res.buyer.variable_mapping[i]) {
                if (j == 'ps_transaction') {
                  this.buyerSettings.ps_transaction =
                    res.buyer.variable_mapping[i][j].outgoing;
                }
                if (j == 'ps_psid') {
                  this.buyerSettings.ps_psid =
                    res.buyer.variable_mapping[i][j].outgoing;
                }
                if (j == 'ps_status') {
                  this.buyerSettings.ps_status =
                    res.buyer.variable_mapping[i][j].outgoing;
                }
                if (j == 'ps_hash') {
                  this.buyerSettings.ps_hash =
                    res.buyer.variable_mapping[i][j].outgoing;
                }
                if (j == 'ps_transaction_id2') {
                  this.buyerSettings.ps_transaction_id2 =
                    res.buyer.variable_mapping[i][j].outgoing;
                }
                if (j == 'ps_transaction_id3') {
                  this.buyerSettings.ps_transaction_id3 =
                    res.buyer.variable_mapping[i][j].outgoing;
                }
                if (j == 'ps_transaction_id4') {
                  this.buyerSettings.ps_transaction_id4 =
                    res.buyer.variable_mapping[i][j].outgoing;
                }
                if (j == 'ps_supplier_id') {
                  this.buyerSettings.ps_supplier_id =
                    res.buyer.variable_mapping[i][j].outgoing;
                }
              }
            }
          }

          this.checkWithUserUrl = this.buyerSettings.ps_transaction + '=';
          this._addUrlTT();
        },
        (err) => {
          const errorMessage = err.error.msg;
          this._toastr.error(errorMessage);
        }
      );
    this._subscriptions.add(buyerSetting$);
  }

  private _addUrlTT() {
    if (this._validateUrl(this.form.value.live_url)) {
      const operationalBuyerUrl = this._getOperationalBuyerUrl(
        this.form.value.live_url,
        this.buyerSettings,
        0
      );
      this.form.patchValue({
        liveUrlTT: `Your URL settings transform the Entry URL into: \n\n ${operationalBuyerUrl}`,
      });
    } else {
      this.form.patchValue({
        liveUrlTT: 'Please enter your Survey URL to check its final form.',
      });
      this.form.value.liveUrlTT =
        'Please enter your Survey URL to check its final form.';
    }
    if (this._validateUrl(this.form.value.test_url)) {
      const operationalBuyerUrl = this._getOperationalBuyerUrl(
        this.form.value.test_url,
        this.buyerSettings,
        1
      );
      this.form.patchValue({
        testUrlTT: `Your URL settings transform the Entry URL into: \n\n ${operationalBuyerUrl}`,
      });
    } else {
      this.form.patchValue({
        testUrlTT: `Your TEST URL is empty, we will use your LIVE URL as the TEST URL. Please add a TEST URL if it is different than the LIVE URL.`,
      });
    }
  }

  private _getOperationalBuyerUrl(
    url: string,
    buyerSettings: any,
    isTest: any
  ) {
    if (
      this._operationalBuyerUrlValidation(this.buyerSettings.ps_transaction)
    ) {
      url = this._getQueryStringUrl(url);
      url += `${this.buyerSettings.ps_transaction}=[%%ps_transaction_id%%]`;
    } else {
      url = this._getQueryStringUrl(url);
      url += 'transaction_id=[%%ps_transaction_id%%]';
    }
    if (this._operationalBuyerUrlValidation(this.buyerSettings.ps_psid)) {
      url = this._getQueryStringUrl(url);
      url += `${this.buyerSettings.ps_psid}=[%%psid%%]`;
    }

    if (this._operationalBuyerUrlValidation(this.buyerSettings.ps_status)) {
      url = this._getQueryStringUrl(url);
      url += this.buyerSettings.ps_status + '=[%%st%%]';
    }

    if (
      this._operationalBuyerUrlValidation(this.buyerSettings.ps_transaction_id2)
    ) {
      url = this._getQueryStringUrl(url);
      url += `${this.buyerSettings.ps_transaction_id2}=[%%ps_transaction_id%%]`;
    }

    if (
      this._operationalBuyerUrlValidation(this.buyerSettings.ps_transaction_id3)
    ) {
      url = this._getQueryStringUrl(url);
      url += this.buyerSettings.ps_transaction_id3 + '=[%%ps_transaction_id%%]';
    }
    if (
      this._operationalBuyerUrlValidation(this.buyerSettings.ps_transaction_id4)
    ) {
      url = this._getQueryStringUrl(url);
      url += `${this.buyerSettings.ps_transaction_id4}=[%%ps_transaction_id%%]`;
    }
    if (
      this._operationalBuyerUrlValidation(this.buyerSettings.ps_supplier_id)
    ) {
      url = this._getQueryStringUrl(url);
      url += `${this.buyerSettings.ps_supplier_id}=[%%ps_supplier_id%%]`;
    }

    return url;
  }

  private _operationalBuyerUrlValidation(id: string) {
    return typeof id !== 'undefined' && id !== '' ? true : false;
  }

  private _getQueryStringUrl(url: any) {
    if (url != '' && url != undefined && url != null) {
      if (url.search(/^http[s]?\:\//) == -1) {
        url = 'http://' + url;
      }
      let re = /[?]/;
      let result = url.match(re);
      if (result && result[0] == '?') {
        let lastChar = url.charAt(url.length - 1);
        if (lastChar != '&') {
          url = url + '&';
        }
        return url;
      } else {
        url = url + '?';
        return url;
      }
    }
  }

  /**
   * @param {newValue}: Current value of the testUrl
   * It is used when value is change of test url or the change of isURLTransform flag
   */
  private _changeLiveUrlInfo(newValue: string) {
    this.warningFlg = false;
    this.showTransIdWarningMsg = false;
    this.showNonHttpsWarningMsg = false;
    let trans_id;
    // url_transform is unchecked then will show the warnings and variable mapping messages
    if (!this.form.value.url_transform) {
      //Do anything with live_url
      if (this._validateNewValue(newValue)) {
        this.form.patchValue({
          liveUrlTT: 'Please enter your Survey URL to check its final form.',
        });
      } else {
        let operationalBuyerUrl = this._getOperationalBuyerUrl(
          newValue,
          this.buyerSettings,
          0
        );
        this.form.patchValue({
          liveUrlTT: `Your URL settings transform the Entry URL into: \n\n ${operationalBuyerUrl}`,
        });
      }
      if (this._validateUrl(this.form.value.live_url)) {
        if (this.form.value.live_url.search(this.regexAmprsnd) != -1) {
          this.warningFlg = true;
          this.showTransIdWarningMsg = true;
          let splitUrl = this.form.value.live_url.split('transaction_id=');
          if (splitUrl[1]) {
            trans_id = `&transaction_id=${splitUrl[1]}`;
          } else {
            trans_id = '&transaction_id=';
          }
        } else {
          if (this.form.value.live_url.search(this.regexQustionMrk) != -1) {
            this.warningFlg = true;
            this.showTransIdWarningMsg = true;
            let splitUrl = this.form.value.live_url.split('transaction_id=');
            if (splitUrl[1]) {
              trans_id = `&transaction_id=${splitUrl[1]}`;
            } else {
              trans_id = '&transaction_id=';
            }
          }
        }
        this.form.patchValue({
          liveWarningMsg: `We think there's an issue with your URL. Please remove the portion that reads ${trans_id} \n\n.\n\nIf you are certain that your link is okay as is, please continue.`,
        });

        this.liveUrlCheck =
          this.form.value.live_url.endsWith('=') ||
          this.form.value.live_url.indexOf('' + this.checkWithUserUrl + '') !==
            -1;
      }
    } else {
      this.form.patchValue({
        liveUrlTT: this.form.value.live_url
          ? `Since you have disabled automatic variable mapping, no variables will be appended to your link.  Respondents will be redirected to: ${this.form.value.live_url}`
          : `Please enter your Survey URL to check its final form.`,
      });
    }
    const regExNonHttpsUrl = new RegExp('http://', 'g');
    if (
      this.form.value.live_url &&
      this.form.value.live_url.search(regExNonHttpsUrl) != -1
    ) {
      this.warningFlg = true;
      this.showNonHttpsWarningMsg = true;
      this.form.patchValue({
        nonHttpsWarningMsg:
          'Please use "https://" instead of "http://" if possible.',
      });
    }
  }

  private _validateNewValue(newValue: string) {
    return typeof newValue === 'undefined' ||
      newValue === '' ||
      newValue === 'null'
      ? true
      : false;
  }

  private _validateUrl(url: string) {
    return url != undefined && url != null && url != '' ? true : false;
  }

  private updateTestUrl(newValue: string) {
    if (
      !newValue &&
      this.finalizeInitialValues.test_url &&
      !this.form.get('test_url')?.dirty
    ) {
      newValue = this.finalizeInitialValues.test_url;
      this.form.patchValue(
        { test_url: this.finalizeInitialValues.test_url },
        { emitEvent: false }
      );
    }
    return newValue;
  }

  public changeTestUrlInfo(newValue: string) {
    const spaceValidator = new SpaceValidator(newValue);
    if (spaceValidator.check()) {
      newValue = spaceValidator.remove();
      this.form.patchValue({
        test_url: newValue,
      });

      this._toastr.error(notifyMessage.errorMessage.URL_SPACE_NOT_ALLOWED);
    }

    this.warningTestFlg = false;
    let trans_id;
    newValue = this.updateTestUrl(newValue);
    // url_transform is unchecked then will show the warnings and variable mapping messages
    if (!this.form.value.url_transform) {
      //Do anything with test_url
      if (this._validateNewValue(newValue)) {
        this.form.patchValue({
          testUrlTT:
            'Your TEST URL is empty, we will use your LIVE URL as the TEST URL. Please add a TEST URL if it is different than the LIVE URL.',
        });
      } else {
        const operationalBuyerUrl = this._getOperationalBuyerUrl(
          newValue,
          this.buyerSettings,
          1
        );
        this.form.patchValue({
          testUrlTT: `Your URL settings transform the Entry URL into: \n\n ${operationalBuyerUrl}`,
        });
      }
      if (this._validateUrl(this.form.value.test_url)) {
        if (this.form.value.test_url.search(this.regexAmprsnd) != -1) {
          this.warningTestFlg = true;
          let splitUrl = this.form.value.test_url.split('transaction_id=');
          if (splitUrl[1]) {
            trans_id = `&transaction_id=${splitUrl[1]}`;
          } else {
            trans_id = '&transaction_id=';
          }
        } else {
          if (this.form.value.test_url.search(this.regexQustionMrk) != -1) {
            this.warningTestFlg = true;
            let splitUrl = this.form.value.test_url.split('transaction_id=');
            if (splitUrl[1]) {
              trans_id = `&transaction_id=${splitUrl[1]}`;
            } else {
              trans_id = '&transaction_id=';
            }
          }
        }

        this.form.patchValue({
          testWarningMsg: `We think there's an issue with your URL. Please remove the portion that reads ${trans_id} \n\n.\n\nIf you are certain that your link is okay as is, please continue.`,
        });
        this.testUrlCheck =
          this.form.value.test_url.endsWith('=') ||
          this.form.value.test_url.indexOf('' + this.checkWithUserUrl + '') !==
            -1;
      }
    } else {
      this.form.patchValue({
        testUrlTT: this.form.value.test_url
          ? `Since you have disabled automatic variable mapping, no variables will be appended to your link.  Respondents will be redirected to: ${this.form.value.test_url}`
          : `Your TEST URL is empty, we will use your LIVE URL as the TEST URL. Please add a TEST URL if it is different than the LIVE URL.`,
      });
    }

    this.updatePayload({ test_url: newValue });
    this._setMultiCountrySurveyData();
  }

  public _updateScheduleLaunchInformation(surveyPayload: SurveyPayload) {
    const scheduleInfo = surveyPayload.schedule_launch?.info ?? {};

    const momentApiCaller = (momentLib: any, ...props: unknown[]) =>
      'default' in momentLib
        ? momentLib.default(...props)
        : momentLib(...props);
    const date = momentApiCaller(
      moment,
      scheduleInfo.dateTimeInISO ?? Date.now()
    )
      .tz(scheduleInfo.timezone ?? moment.tz.guess())
      .format('YYYY-MM-DD');

    this.form.patchValue({
      scheduledLaunchDetails: {
        date: date,
        hours: this._formatTimeToPresentation(scheduleInfo.hours ?? undefined),
        minutes: this._formatTimeToPresentation(
          scheduleInfo.minutes ?? undefined
        ),
        timezone: scheduleInfo.timezone ?? undefined,
      },
    });

    this.updatePayload({
      schedule_launch: this._mapScheduleLaunchPayload(),
    });
  }

  public selectScheduledLaunchDate(value: SelectDate) {
    this.form.patchValue({
      scheduledLaunchDetails: {
        date: this._datepipe.transform(value.start._d, 'yyyy-MM-dd'),
      },
    });

    this.updatePayload({
      schedule_launch: this._mapScheduleLaunchPayload(),
    });
  }

  private _formatTimeToPresentation(
    value: number | undefined
  ): string | undefined {
    if (value == undefined || value == null) {
      return;
    }

    return value < 10 ? `0${value}` : value.toString();
  }

  public datapickerOptions = {
    locale: { format: 'YYYY-MM-DD' },
    singleDatePicker: true,
    alwaysShowCalendars: false,
    minDate: new Date(),
  };

  private _createDateRangePickerOption() {
    this.filterByDatapickerOptions = {
      locale: { format: 'YYYY-MM-DD' },
      alwaysShowCalendars: false,
      showDropdowns: true,
    };
  }

  private _assignTimeField() {
    //get hours
    for (let i = 0; i < 24; i++) {
      this.hours.push({ key: i < 10 ? `0${i}` : i.toString() });
    }

    //get minutes
    for (let i = 0; i < 60; i++) {
      this.minutes.push({ key: i < 10 ? `0${i}` : i.toString() });
    }

    //get timezone
    for (let i = 0; i < moment.tz.names().length; i++) {
      let tzObj = {
        index: i,
        name:
          ' (GMT' +
          moment.tz(moment.tz.names()[i]).format('Z') +
          ') ' +
          moment.tz.names()[i],
        value: moment.tz.names()[i],
      };
      this.tzNames.push(tzObj);
    }
  }

  public selectTimeZone(timezone: TimeZone) {
    this.form.patchValue({
      scheduledLaunchDetails: {
        timezone: timezone.value,
      },
    });
    this.updatePayload({
      schedule_launch: this._mapScheduleLaunchPayload(),
    });
  }

  public selectHours(value: SelectTime) {
    this.form.patchValue({
      scheduledLaunchDetails: {
        hours: value.key,
      },
    });
    this.updatePayload({
      schedule_launch: this._mapScheduleLaunchPayload(),
    });
  }

  public selectMinutes(value: SelectTime) {
    this.form.patchValue({
      scheduledLaunchDetails: {
        minutes: value.key,
      },
    });
    this.updatePayload({
      schedule_launch: this._mapScheduleLaunchPayload(),
    });
  }

  private _createQbpSelectionQuotas(quotas: Quotas[]) {
    if (quotas.length) {
      // Group quotas by category(eg: autoNested, layered)
      const groupedQuota = quotas.reduce((acc: any, quota: Quotas) => {
        const key = quota['quota_category'];
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(quota);
        return acc;
      }, {});
      const layeredAndGroupedQuotas = [
        ...(groupedQuota.layered || []),
        ...(groupedQuota.grouped || []),
      ];
      this._prepareQbpLayeredAndGroupedQuotas(layeredAndGroupedQuotas);
      this._prepareQbpAdvanceQuotas(groupedQuota.advance);
      // Sorting quotas to show in the QBP list
      this.qbpSelectionQuotas.sort((a, b) => a.name.localeCompare(b.name));
      this._prepareQbpNestedQuotas(groupedQuota.autoNested);
      this._patchSelectedQuota();
      this._setQbpSelectionDetails(this.form.value.qbp);
    }
  }

  private _prepareQbpLayeredAndGroupedQuotas(layeredQuotas: Quotas[] = []) {
    const layeredQuotasByQualification = layeredQuotas.reduce(
      (acc: any, quota: Quotas) => {
        const key =
          quota.criteria.length === 1 && quota.group_key !== 'Children'
            ? quota.criteria[0].qualification_name
            : 'children';
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(quota);
        return acc;
      },
      {}
    );

    if (
      layeredQuotasByQualification &&
      Object.keys(layeredQuotasByQualification).length
    ) {
      for (const qualName in layeredQuotasByQualification) {
        let tempObj = <QbpSelectionQuota>{};
        tempObj.name = tempObj.category = this._capitalizeFirstLetter(
          qualName.replace(/ /g, '')
        );
        const qualificationData: AidaQualifications = {};
        tempObj.selectedQuotaIds = layeredQuotasByQualification[qualName].map(
          (quota: Quotas) => {
            quota.criteria.forEach(
              (c) =>
                (qualificationData[c.qualification_code] =
                  this.getAidaMappedQualificationCode(c.qualification_code))
            );
            return quota['buyer_quota_id'];
          }
        );
        tempObj.qualificationData = qualificationData;
        this.qbpSelectionQuotas.push(tempObj);
      }
    }
  }

  getAidaMappedQualificationCode(
    qualificationCode: number
  ): AidaMappedQualification {
    return this._aidaPayloadService.getMappedDBQualificationCodes()[
      qualificationCode
    ];
  }

  private _prepareQbpAdvanceQuotas(advanceQuotas: Quotas[] = []) {
    const advanceQuotasGroupByQualName = advanceQuotas.reduce(
      (acc: any, advanceQuota: Quotas) => {
        const key = advanceQuota.criteria[0].qualification_name;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(advanceQuota);
        return acc;
      },
      {}
    );

    if (
      advanceQuotasGroupByQualName &&
      Object.keys(advanceQuotasGroupByQualName).length
    ) {
      for (const qualName in advanceQuotasGroupByQualName) {
        let tempObj = <QbpSelectionQuota>{};
        tempObj.name = tempObj.category = this._capitalizeFirstLetter(
          qualName.toLowerCase().replace(/ /g, '_')
        );
        tempObj.selectedQuotaIds = advanceQuotasGroupByQualName[qualName].map(
          (quota: Quotas) => quota['buyer_quota_id']
        );
        this.qbpSelectionQuotas.push(tempObj);
      }
    }
  }

  private _prepareQbpNestedQuotas(nestedQuotas: Quotas[] = []) {
    if (nestedQuotas && nestedQuotas.length) {
      let tempObj = <QbpSelectionQuota>{};
      tempObj.category = 'autoNested';
      const qualificationData: AidaQualifications = {};
      let qualNames = nestedQuotas[0].criteria.map((criteria: Criteria) => {
        qualificationData[criteria.qualification_code] =
          this.getAidaMappedQualificationCode(criteria.qualification_code);
        return criteria['qualification_name'];
      });
      qualNames = qualNames.map((qualName: string) => {
        return this._capitalizeFirstLetter(qualName.replace(/ /g, ''));
      });
      tempObj.name = `Nested - ${qualNames.toString()}`;
      tempObj.selectedQuotaIds = nestedQuotas.map(
        (quota: Quotas) => quota['buyer_quota_id']
      );
      tempObj.qualificationData = qualificationData;
      this.qbpSelectionQuotas.unshift(tempObj);
    }
  }

  private _patchSelectedQuota() {
    this.form.controls.qbpSelectedQuota.setValue(this.qbpSelectionQuotas[0]);
    this.updatePayload({
      qbp: {
        enable: this.form.value.qbp.enable,
        quota_ids: this.form.value.qbpSelectedQuota.selectedQuotaIds || [],
      },
    });
  }

  private _capitalizeFirstLetter(s: string) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  private _setQbpSelectionDetails(qbp: Qbp) {
    if (qbp.category && qbp.selectedQuotaIds && qbp.selectedQuotaIds.length) {
      const defaultSelection = this.qbpSelectionQuotas.find(
        (i: QbpSelectionQuota) => {
          if (
            i.category.toLowerCase().trim().replace(/_/g, '') ===
            'householdincome'
          ) {
            return i.category
              .toLowerCase()
              .trim()
              .replace(/_/g, '')
              .includes(qbp.category.toLowerCase().trim().replace(/_/g, ''));
          }
          return (
            i.category.toLowerCase().trim().replace(/_/g, '') ===
            qbp.category.toLowerCase().trim().replace(/_/g, '')
          );
        }
      );
      if (defaultSelection) {
        this.form.patchValue({
          qbpSelectedQuota: defaultSelection,
        });
        this.updatePayload({
          qbp: {
            enable: this.form.value.qbp.enable,
            quota_ids: this.form.value.qbpSelectedQuota.selectedQuotaIds || [],
          },
        });
      } else {
        this._setActiveQuotaSelectionQbp();
      }
    }

    if (
      this.form.value.qbpSelectedQuota &&
      !Object.keys(this.form.value.qbpSelectedQuota).length
    ) {
      this.form.patchValue({
        qbpSelectedQuota: {
          category: qbp.category ? qbp.category : '',
          selectedQuotaIds: [],
        },
      });
      this.updatePayload({
        qbp: {
          enable: this.form.value.qbp.enable,
          quota_ids: this.form.value.qbpSelectedQuota.selectedQuotaIds || [],
        },
      });
    }
    if (!this.qbpSelectedQuotaCategory && this.form.value.qbpSelectedQuota) {
      this.qbpSelectedQuotaCategory = this.form.value.qbpSelectedQuota.category;
    }
  }

  private _setActiveQuotaSelectionQbp() {
    let nestedQuotaIndex = 0;
    if (this.qbpSelectedQuotaCategory) {
      nestedQuotaIndex = this.qbpSelectionQuotas.findIndex(
        (e: QbpSelectionQuota) => e.category == this.qbpSelectedQuotaCategory
      );
    } else {
      nestedQuotaIndex = this.qbpSelectionQuotas.findIndex(
        (e: QbpSelectionQuota) => e.category == 'autoNested'
      );
    }
    if (nestedQuotaIndex >= 0) {
      this.form.patchValue({
        qbpSelectedQuota: this.qbpSelectionQuotas[nestedQuotaIndex],
      });
    } else {
      this.form.patchValue({
        qbpSelectedQuota: this.qbpSelectionQuotas[0],
      });
    }
    this.updatePayload({
      qbp: {
        enable: this.form.value.qbp.enable,
        quota_ids: this.form.value.qbpSelectedQuota.selectedQuotaIds || [],
      },
    });
  }

  public updateQBPToggle() {
    if (this.qbpSelectionQuotas.length) {
      this.form.patchValue({
        qbp: {
          enable: !this.form.value.qbp.enable,
        },
      });
      this.updatePayload({
        qbp: {
          enable: this.form.value.qbp.enable,
          quota_ids: this.form.value.qbpSelectedQuota.selectedQuotaIds || [],
        },
      });
    } else {
      let qbpValidationText =
        'To enable QUOTA-BASED PRICING you need to have set quotas on your project.';
      if (!this.isInsight) {
        qbpValidationText +=
          " Please use the bottom navigation to go back to the 'Define Audience'section and build your audience quotas.";
      }
      const modalRef = this._modal.open(ConfirmationModalComponent, {
        data: {
          text: qbpValidationText,
        },
        maxWidth: '450px',
      });

      modalRef.onClose$.subscribe((msg) => {
        this.form.patchValue({
          qbp: {
            enable: false,
          },
        });
        this.updatePayload({
          qbp: {
            enable: this.form.value.qbp.enable,
            quota_ids: this.form.value.qbpSelectedQuota?.selectedQuotaIds || [],
          },
        });
      });
    }
  }

  public updateQLCToggle() {
    if (
      this.surveyDatails &&
      this.surveyDatails.quotas &&
      this.surveyDatails.quotas.length
    ) {
      this.form.patchValue({
        qlc: {
          enable: !this.form.value.qlc.enable,
        },
      });
      this.updatePayload({
        qlc: {
          enable: this.form.value.qlc.enable,
          multiple_quotas: {
            enable: false,
            count_by_quota: false,
            compensate_by_quota: false,
          },
        },
      });
    } else {
      const modalRef = this._modal.open(ConfirmationModalComponent, {
        data: {
          text: "To enable QUOTA-LEVEL COMPLETE tracking you need to have set quotas on your project. Please use the bottom navigation to go back to the 'Define Audience' section and build your audience quotas.",
        },
        maxWidth: '450px',
      });

      modalRef.onClose$.subscribe((msg) => {
        this.form.patchValue({
          qlc: {
            enable: false,
          },
        });
        this.updatePayload({
          qlc: {
            enable: this.form.value.qlc.enable,
            multiple_quotas: {
              enable: false,
              count_by_quota: false,
              compensate_by_quota: false,
            },
          },
        });
      });
    }
  }

  public setQbpSelection(quota: QbpSelectionQuota) {
    this.form.patchValue({
      qbpSelectedQuota: quota,
    });

    this.updatePayload({
      qbp: {
        enable: this.form.value.qbp.enable,
        quota_ids: this.form.value.qbpSelectedQuota.selectedQuotaIds || [],
      },
    });
  }

  private _manageLaunchEnableDisable(surveyDetail: BuyerSurveyResponse) {
    this.fieldOverTimeDisable = surveyDetail.soft_launch.enable;
    if (
      surveyDetail.ps_survey_status != SURVEY_STATUS.DRAFT ||
      surveyDetail.is_feot_enabled
    ) {
      this.scheduleLaunchSlideDisable = true;
    }
    this._scheduleLaunchDisableCheck(surveyDetail);
    if (
      surveyDetail.schedule_launch.enable &&
      surveyDetail.ps_survey_status === SURVEY_STATUS.DRAFT &&
      surveyDetail.scheduledLaunchDetails &&
      surveyDetail.scheduledLaunchDetails.timezone
    ) {
      this.softLaunchDisable = true;
    }
    if (this.softLaunchDisable || surveyDetail.is_feot_enabled) {
      this.softLaunchSlideDisable = true;
    }
    this.isQbpSliderDisabled =
      surveyDetail.ps_survey_status !== SURVEY_STATUS.DRAFT ||
      this.surveyDatails.price_reco ||
      !!this.surveyDatails.mc?.SVP;
    this.isQlcSliderDisabled =
      surveyDetail.ps_survey_status !== SURVEY_STATUS.DRAFT ||
      !!this.surveyDatails.mc?.SVP;
    this.form.value.qbp.enable =
      this.surveyDatails.price_reco || this.surveyDatails.is_qbp_enable;

    this.isMultiNestingAvailable = Common.isMultiNestedQuotasAvailable(
      surveyDetail.quotas
    );
  }

  private _scheduleLaunchDisableCheck(surveyDetail: BuyerSurveyResponse) {
    if (
      surveyDetail.ps_survey_status != SURVEY_STATUS.DRAFT ||
      (surveyDetail.schedule_launch.enable && surveyDetail.end_date)
    ) {
      this.scheduleLaunchDisable = true;
    }
  }

  public updatePayload(payload: any | null = null) {
    if (payload) {
      if (this._initDone) {
        this._surveyMetaDataService.setIsFormDirty(true);
      }
      this.surveyPayload = Object.assign(this.surveyPayload, payload);
    }
  }

  private _mapScheduleLaunchPayload(
    scheduleLaunch = this.form.value.schedule_launch,
    scheduledLaunchDetails = this.form.value.scheduledLaunchDetails
  ) {
    if (!scheduleLaunch.enable) {
      return {
        enable: false,
      };
    }

    return {
      enable: true,
      info: {
        date: scheduledLaunchDetails.date || undefined,
        hours: Number(scheduledLaunchDetails.hours) ?? undefined,
        minutes: Number(scheduledLaunchDetails.minutes) ?? undefined,
        timezone: scheduledLaunchDetails.timezone || undefined,
      },
    };
  }

  private _mapSoftLaunchPayload(
    isSoftLaunchEnabled: boolean,
    launchQuantity: number
  ) {
    return {
      soft_launch: {
        enable: isSoftLaunchEnabled,
        quantity: launchQuantity,
      },
    };
  }

  public changeInclExcl(flagValue: number) {
    this.inclExclDisable = !!flagValue;
    if (this.enableFilteringByDate) {
      this.filterDateRange = {};
      this._setCurrentDateRangePicker();
      // If PSIDs are upload by survey grouping, then emptying them
      if (this.surveyGroupingPSIDUpload) {
        this._psids = [];
        this._psIdsCommand$.next([]);
      }
      this.surveyGroupingPSIDUpload = false;
      this.form.patchValue({ incl_excl: flagValue, survey_grouping: [] });
      this.updatePayload({ incl_excl: flagValue, survey_grouping: [] });
      this._updateSurveyData();
    } else {
      this._setInclExcl(flagValue);
    }
  }

  private _setInclExcl(incl_excl: number) {
    this.inclExclDisable = !!incl_excl;
    this.form.patchValue({ incl_excl });
    this.updatePayload({ incl_excl });
  }

  public changeSurveyDQEnabled(flag: boolean) {
    this.form.patchValue({
      is_dq_enable: flag,
    });
    this.updatePayload({
      is_dq_enable: flag,
    });
  }

  public clearPSID() {
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: 'Are you sure you want to clear PSIDs?',
        cancelButtonLabel: 'Cancel',
        confirmButtonLabel: 'Clear PSIDs',
      },
      maxWidth: '400px',
      width: '373px',
    });
    modalRef.onClose$.subscribe((msg) => {
      if (msg == 'ok') {
        this._buyerApiService
          .clearUploadedPSIDs(this.surveyId)
          .subscribe((resp: ClearPsidsResponse) => {
            this._toastr.success('Sucess! PSIDs have been cleared');
            this.updatePayload({
              ref_list: [],
              hasPSIDRefList: false,
            });
            this._psIdsCommand$.next([]);
            this.form.patchValue({ ref_list: [], hasPSIDRefList: false });
            this.inclExclBuyerUpload = false;
            this._sharedDataService.setPsidModelClose(false);
          });
      }
      if (msg == 'cancel') {
        this._sharedDataService.setPsidModelClose(true);
      }
    });
  }

  public viewPSID() {
    const modalRef = this._modal.open(ClearOrViewPsidModalComponent, {
      data: {
        psids: this._psids,
        isClearRequest: false,
      },
      height: '80%',
      width: '80%',
    });
  }

  public viewIncludePSID() {
    this._modal.open(IncludeListComponent, {
      data: {
        psids: this._includePsids,
      },
      height: '80%',
      width: '80%',
    });
  }

  public onFileChange(event: Event) {
    const element = <HTMLInputElement>event.currentTarget;
    this.file = (element.files as FileList)[0];
    if (element.files !== null) {
      this.fileName = this.file?.name;
      const fileExtension = this.fileName?.split('.').pop();
      if (element.files.length == 0) {
        this._toastr.warning(notifyMessage.warningMessage.NO_FILE);
        return;
      }
      if (this._validateFileExtention(this.file)) {
        this._uploadPsidList(this.file, event);
      }
    }
  }

  private _uploadPsidList(file: File, event: Event) {
    this._fdata = new FormData(); // clear _fdata
    this._fdata.append('psid_ref_file', file);

    this._buyerApiService
      .uploadPSIDRefFile(this.surveyId, this._fdata)
      .subscribe(
        (_res: PsidsUploadRefListResponse) => {
          this._toastr.success(notifyMessage.successMessage.FILE_ADDED);
          this.inclExclBuyerUpload = true;
          this.form.patchValue({
            hasPSIDRefList: true,
          });
          this._isQbpQualified();
          event.preventDefault();

          this._buyerApiService
            .getBuyerUploadedList(
              this.surveyDatails.ps_survey_id,
              this.surveyDatails.incl_excl
            )
            .subscribe((psidsResp: PsidsBuyerUploadListResponse) => {
              if (psidsResp && psidsResp.psids && psidsResp.psids.length) {
                this._psids = psidsResp.psids;
                this._updateSurveyPayloadWithPsids();
                this._psIdsCommand$.next(this._psids);
              }
            });
        },
        (err) => {
          const errorMessage = err.error.msg;
          if (errorMessage) {
            this._toastr.error(errorMessage);
          } else {
            this._toastr.error('Sorry, we had an unexpected error');
          }
        }
      );
  }

  private _updateSurveyPayloadWithPsids() {
    this._includePsids = this._psids.map((resp) => resp.PSID);
    const updatePayload = {
      ref_list: this._mapPsIdsPaylod(this._psids),
    } as SurveyPayload;
    this._surveyMetaDataService.sendSurveyPayload(updatePayload);
  }

  private _mapPsIdsPaylod(psIds: PSIDs[]) {
    return psIds.map((resp) => {
      return { psid: resp.PSID };
    });
  }

  private _isQbpQualified() {
    if (
      this.manageEdit &&
      this.surveyDatails.is_qbp_enable &&
      this.form.value.uniqBuyerEntryLinkFlag
    ) {
      this.form.patchValue({
        uniqBuyerEntryLinkFlag: false,
      });
    }
    if (
      this.surveyDatails.is_qbp_enable &&
      (this.surveyDatails.incl_excl || this.form.value.uniqBuyerEntryLinkFlag)
    ) {
      this.form.patchValue({
        is_qbp_enable: false,
      });
    }
  }

  private _validateFileExtention(file: File) {
    const validExt = new Array('.xls', '.xlsx', '.csv');
    if (!file) {
      this._toastr.error(notifyMessage.errorMessage.FILE_UPLOAD_MISSING);
      return false;
    }
    const fileExt = file.name.substring(file.name.lastIndexOf('.'));
    if (validExt.indexOf(fileExt) < 0) {
      this._toastr.error(
        notifyMessage.errorMessage.FILE_UPLOAD_ASK_EXTERNAL_BUYER
      );
      return false;
    }
    return true;
  }

  public setSurveyGrouping() {
    const surveyIds = this._getSurveysIdsFromForm();
    this._performActionsOnSurveyGroupingChange(surveyIds);
    if (this.enableFilteringByDate) {
      this._clearFilterByDateRange();
    }
    this._updateSurveyData();
  }

  private _getSurveysIdsFromForm() {
    return this._mapSurveyGroupingPayload(this.form.value.survey_grouping);
  }

  private _mapSurveyGroupingPayload(surveyGrouping: string | Array<number>) {
    if (Array.isArray(surveyGrouping)) {
      return surveyGrouping;
    }

    return surveyGrouping
      .split(',')
      .map((id: string) => +id)
      .filter(Number);
  }

  private _performActionsOnSurveyGroupingChange(surveyIds: number[]) {
    this.updatePayload({
      survey_grouping: surveyIds,
    });
    const payload = {
      surveyIds,
    };
    this._getIncludePsids(payload);
  }

  public setSubscriberEmails() {
    this.updatePayload({
      subscriber_emails: this._mapEmails(this.form.value.subscriber_emails),
    });
  }

  private _mapEmails(emails: string | Array<string>): Array<string> {
    if (Array.isArray(emails)) {
      return emails;
    }

    return emails.split(',');
  }
  public _getGroupSurveyIds(
    startDate: string,
    endDate: string,
    locale: LocaleLanguageCountry
  ) {
    this._buyerApiService
      .getGroupSurveys(startDate.toString(), endDate.toString(), locale)
      .subscribe(
        (response) => {
          if (response) {
            const surveyIds: Array<number> = [];
            surveyIds.push(...response.map((resp) => Number(resp.id)));
            this.form.patchValue({
              survey_grouping: surveyIds,
            });
            this.includePostPayload = {
              fromDate: startDate.toString(),
              toDate: endDate.toString(),
              surveyIds: surveyIds,
            };
            this._getIncludePsids(this.includePostPayload);
            this._updateSurveyData();
          }
        },
        (error: any) => {
          this._toastr.error(error.error.msg);
        }
      );
  }

  public applyFilterDateRange(value: FilterDateRange) {
    this.filterDateRange.start = new Date(value.picker.startDate._d);
    this.filterDateRange.end = new Date(value.picker.endDate._d);
    const { start, end } = this._getStartEndDate();
    if (
      this.form.value.survey_grouping &&
      this.form.value.survey_grouping.length
    ) {
      this._toastr.warning(
        notifyMessage.warningMessage.EXCLUSION.CHANGING_DATE
      );
    }
    this._getGroupSurveyIds(String(start), String(end), this._locale);
    this._activityLogService
      .post({
        surveyId: this.surveyId,
        logMessage: 'INCLUDE_EXCLUDE_DATE_FILTER',
        metadata: {
          from: this.filterDateRange.start,
          to: this.filterDateRange.end,
        },
      })
      .subscribe();
  }

  public cancelFilterDateRange() {
    this.form.patchValue({
      survey_grouping: [...new Set(this.surveyDatails.survey_grouping)],
    });
    this._includePsids = [];
    this.filterDateRange = {};
    this._setCurrentDateRangePicker();
    this._updateSurveyData();
  }

  private _verifyCompanySettings(companyId: number) {
    this._buyerConfigService
      .getBuyerConfig()
      .subscribe((buyerConfigResponse: GetBuyerConfigResponse) => {
        this.isEnableMarginMaxForBuyer =
          !!buyerConfigResponse?.data?.enableMarginMax?.includes(companyId);
        this.isEnableBillingNumberRulesForBuyer =
          !!buyerConfigResponse?.data?.enableBillingNumberRules?.includes(
            companyId
          );
        this.enableMandatoryBillingNumber =
          !!buyerConfigResponse?.data?.enableMandatoryBillingNumber?.includes(
            companyId
          );
        this.enableSupplierAllocationThrottling =
          this._env.environment.ZAPPI_STORE_BUYER_IDS.includes(companyId) &&
          !SURVEY_LAUNCH_CONSTANTS.ZAPPI_ALLOCATION_THROTTLING_COUNTRIES.includes(
            this.surveyDatails.locale['country_code']
          )
            ? false
            : !!buyerConfigResponse?.data?.enableSupplierAllocationThrottling?.includes(
                companyId
              );
        this._surveyCompanySubject.next(companyId);
        this._setBillingNumberRules();
      });
  }

  private _updateSurveyData() {
    const { start = '', end = '' } = this._getStartEndDate();
    let updatePayload = {
      ref_list: this._includePsids.map((psid) => {
        return { psid };
      }),
      feasibility_exclusions: {
        enable: false,
        start,
        end,
        criterias: this.surveyDatails.feasibility_exclusions?.criterias,
      },
      survey_grouping: this._getSurveysIdsFromForm(),
    } as SurveyPayload;
    this._surveyMetaDataService.sendSurveyPayload(updatePayload);
  }

  private _getStartEndDate() {
    let { start = '', end = '' } = this.filterDateRange;
    if (start && end) {
      start = this._commonService.formatDate(start as Date);
      end = this._commonService.formatDate(end as Date);
    }
    return { start, end };
  }

  private _getIncludePsids(payload: IncludePostPayload) {
    if (!this.inclExclDisable) {
      return;
    }
    if (!this._validatePayload(payload)) {
      return;
    }
    this._buyerApiService
      .getIncludePsids(payload)
      .pipe(filter((psidsResponse) => !!psidsResponse))
      .subscribe(
        (psidsResponse: IncludePsidApiResponse) => {
          this._includePsids = psidsResponse.ref_list || [];
          this.surveyGroupingPSIDUpload = true;
          this.form.patchValue({
            survey_grouping: psidsResponse.survey_ids || [],
          });
          this._updateSurveyData();
          if (!this._includePsids?.length) {
            this._toastr.error(notifyMessage.errorMessage.NO_PSIDS_FOUND);
          }
        },
        (err) => {
          this._toastr.error(err.error.ps_api_response_message);
        }
      );
  }

  public updateMarginMax(isEnableMarginMax: boolean) {
    this.form.patchValue({
      is_enable_margin_max: isEnableMarginMax,
    });
    this.updatePayload({
      is_enable_margin_max: isEnableMarginMax,
    });
  }

  public updateFeotCustomPacing(isCustomPacingEnabled: boolean) {
    this.form.patchValue({
      feot: {
        is_custom: isCustomPacingEnabled,
      },
    });

    this.updatePayload({
      feot: {
        ...this.form.value.feot,
        is_custom: isCustomPacingEnabled,
      },
    });
    this._calculateFeotFields();
  }

  get isCustomPacingText(): Boolean {
    return this.form.value.feot.is_custom && this.form.value.is_feot_enabled;
  }

  public updateFeotPacing() {
    this.updatePayload({
      feot: {
        ...this.form.value.feot,
        interval: this.form.value.feot.interval,
      },
    });
    this._calculateFeotFields();
  }

  public updateFeotPacingInterval() {
    this.updatePayload({
      feot: {
        ...this.form.value.feot,
        interval: this.form.value.feot.interval,
      },
    });
    this._calculateFeotFields();
  }

  public feotPaceFormat(pace?: string, interval: number = 0) {
    if (!pace) {
      pace = this.form.value.feot.pacing;
    }
    if (!interval) {
      interval = this.form.value.feot.interval;
    }
    return interval > 1 ? pace : this.feotPaceSingular();
  }

  public feotPacePlural() {
    return this.feotFields.feotQuantity > 1 ? 's' : '';
  }

  public feotPaceSingular() {
    const { pacing: pace } = this.form.value.feot;
    return pace?.substring(0, pace.length - 1);
  }

  private _clearFilterByDateRange() {
    if (this.filterDateRange.start && this.filterDateRange.end) {
      this.filterDateRange = {};
      this._setCurrentDateRangePicker();
      this._toastr.warning(
        notifyMessage.warningMessage.EXCLUSION.REMOVE_SURVEY_NUMBER
      );
    }
  }

  private _setFilterDateRange() {
    const { start, end } = this.surveyDatails.feasibility_exclusions || {};
    if (start) {
      this.filterDateRange.start = this._momentApiCaller(moment, start)
        .tz(moment.tz.guess())
        .format('YYYY-MM-DD');
      this._dateRangeFilterRef?.datePicker.setStartDate(
        this._momentApiCaller(moment, start)
      );
    }
    if (end) {
      this.filterDateRange.end = this._momentApiCaller(moment, end)
        .tz(moment.tz.guess())
        .format('YYYY-MM-DD');
      this._dateRangeFilterRef?.datePicker.setEndDate(
        this._momentApiCaller(moment, end)
      );
    }
  }

  private _momentApiCaller(momentLib: any, ...props: unknown[]) {
    return 'default' in momentLib
      ? momentLib.default(...props)
      : momentLib(...props);
  }

  public copyToClipboard = (text: string) => {
    this.showSuccessTooltip = true;
    navigator.clipboard.writeText(text);
    setTimeout(() => {
      this.showSuccessTooltip = false;
    }, 2000);
  };

  private _validatePayload(payload: IncludePostPayload) {
    if (!((payload.fromDate && payload.toDate) || payload.surveyIds?.length)) {
      this._includePsids = [];
      this.surveyGroupingPSIDUpload = false;
      this.form.patchValue({ survey_grouping: [] });
      this._updateSurveyData();
      return false;
    }
    return true;
  }

  public handleEditBasicToken(show: boolean = true) {
    this.notEditableBasicToken = show;
    if (!show) {
      this.basicToken.nativeElement.focus();
    } else {
      this.form.patchValue({
        basic_token: {
          enable: true,
          value: this.fixedBasicTokenValue,
        },
      });
    }
  }

  private _setCurrentDateRangePicker() {
    const currentDate = this._momentApiCaller(moment, Date.now()).tz(
      moment.tz.guess()
    );
    this._dateRangeFilterRef?.datePicker.setStartDate(currentDate);
    this._dateRangeFilterRef?.datePicker.setEndDate(currentDate);
  }

  validateBillingCode() {
    const buyer_company = this.surveyPayload.buyer_company || 0;
    if (!this.form.value.billing_id) {
      return;
    }
    this._buyerApiService
      .serachBuyerSurveys(
        SurveyStatus.Invoice,
        this.form.value.billing_id,
        buyer_company,
        true
      )
      .subscribe(
        (invoicedSurveys: BuyerSurveyResponse[]) => {
          invoicedSurveys = invoicedSurveys.filter(
            (invoicedSurvey) =>
              invoicedSurvey.billing_id != this.surveyDatails.billing_id
          );
          if (invoicedSurveys.length > 0) {
            this._toastr.warning(
              notifyMessage.warningMessage.LAUNCH_SURVEY
                .BILLING_CODE_ALREADY_USED
            );
          }
        },
        (err) => {
          this._toastr.error(err.error.msg);
        }
      );
  }

  updateDqSetting(setting: string, value: boolean) {
    this.form.patchValue({
      dq_settings: {
        [setting]: value,
      },
    });
    this._updateDataQualityPayload();
  }

  private _updateDataQualityPayload() {
    this.updatePayload({
      dq_settings: {
        purescore_filter_enabled:
          this.form.value.dq_settings.purescore_filter_enabled,
        fp_device_check: this.form.value.dq_settings.fp_device_check,
        fp_fraud_check: this.form.value.dq_settings.fp_fraud_check,
      },
    });
  }

  public toggleSameSettingsCheck(key: string, value: boolean) {
    this.form.patchValue({
      [key]: value,
    });
  }

  ngAfterContentChecked(): void {
    this._cd.detectChanges();
  }

  public onSameFileEntryLinksCheckChange() {
    const { isSameFileEntryLinksChecked } = this.form.value;
    // Toggle the checkbox value
    this.form.patchValue({
      isSameFileEntryLinksChecked: !isSameFileEntryLinksChecked,
    });
    // Update the payload with the toggled value
    this.updatePayload({
      is_same_file_entry_links: !isSameFileEntryLinksChecked,
    });

    if (this._isMcSvpSurvey()) {
      this._sendMcSpvDataForUpdate(
        { is_same_file_entry_links: !isSameFileEntryLinksChecked },
        true
      );
    }

    this._updateUniqueBuyerEntryLink();
  }

  private _clearBuyerEntryLink() {
    this.buyerEntryLinksArr = [];
    this._buyerEntryLinksCommand$.next([]);
    this.buyerEntryLinkUploaded = false;
    const testEntryLink = '';
    this.form.patchValue({
      test_ps_survey_entry_link: testEntryLink,
    });
    this.isBuyerLinkSliderDisabled = false;
    this.updatePayload({
      unique_links: {
        enable: this.form.value.uniqBuyerEntryLinkFlag,
        items: this.buyerEntryLinksArr,
      },
      test_ps_survey_entry_link: testEntryLink,
    });
  }

  private _setEntryLinkItem(childSurveys: BuyerSurveyResponse[]) {
    const selectedMcSpvChild = childSurveys.find(
      (childSurvey: BuyerSurveyResponse) =>
        Number(childSurvey.ps_survey_id) === Number(this.surveyId)
    );
    if (selectedMcSpvChild && !selectedMcSpvChild.unique_links?.items?.length) {
      this._clearBuyerEntryLink();
    }
  }

  private _updateUniqueBuyerEntryLink() {
    if (!this.form.value.isSameFileEntryLinksChecked) {
      this._clearBuyerEntryLink();
      return;
    }
    const { unique_links } = this.surveyPayload;

    if (
      unique_links?.enable &&
      unique_links.items.length > 0 &&
      this.form.value.isSameFileEntryLinksChecked
    ) {
      if (this._isMcSvpSurvey()) {
        this._updateUniqueBuyerEntryLinkMcSPVPayLoad();
      }
      this._surveyMetaDataService.saveSurvey();
    }
  }

  private _updateUniqueBuyerEntryLinkMcSPVPayLoad() {
    const { isSameFileEntryLinksChecked } = this.form.value;

    const update = {
      unique_links: this.surveyPayload.unique_links,
      is_same_file_entry_links: !!isSameFileEntryLinksChecked,
    };
    const isUpdateAllLocale = !!isSameFileEntryLinksChecked;
    this._sendMcSpvDataForUpdate(update, isUpdateAllLocale);
  }

  ngOnDestroy() {
    this._missingMarkerModelRef?.close();
    this._subscriptions.unsubscribe();
  }

  public featureStatus(value: boolean) {
    return value ? 'ON' : 'OFF';
  }

  public isAidaFeatureEnabled(aida?: AidaPayload) {
    return (
      !!this._auth.buyerConfig?.enableAiDeliveryAssistance ||
      (this._auth.userType === 'operator' &&
        !this._auth.isLoggedInAsServiceBuyer()) ||
      aida?.enabled
    );
  }

  private _getAidaEligibility$(): Observable<AidaEligibilityInfo> {
    const elegibility$ = this._surveyDetailsCommand$.asObservable().pipe(
      map(({ ps_survey_id, ps_survey_status, aida, quotas = [] }) => ({
        surveyId: ps_survey_id,
        ps_survey_status,
        aida,
        quotas,
      })),
      switchMap(({ surveyId, ps_survey_status, aida, quotas }) => {
        if (!this.isAidaFeatureEnabled(aida)) {
          return EMPTY;
        }
        if (ps_survey_status !== SURVEY_STATUS.DRAFT) {
          return of({
            eligible: true,
            error_messages: [],
          });
        }
        return this._aidaService.checkSurveyEligibility(surveyId);
      })
    );

    return combineLatest([
      elegibility$,
      this.form.get('is_feot_enabled')!.valueChanges,
      this.form.get('qbp.enable')!.valueChanges,
      this.form.get('is_enable_margin_max')!.valueChanges,
      this.form.get('softLaunch')!.valueChanges,
    ]).pipe(
      map(
        ([
          eligibility,
          isFeotEnabled,
          isQbpEnabled,
          isMarginMaxEnabled,
          isSoftLaunchEnabled,
        ]) => ({
          eligibility,
          features: {
            isFeotEnabled,
            isQbpEnabled: this._shouldDisableQbp(isQbpEnabled),
            isMarginMaxEnabled:
              isMarginMaxEnabled && this.userType === 'operator',
            isSoftLaunchEnabled,
          },
        })
      )
    );
  }

  private _handleAidaDetails() {
    const _subscription = this._aidaPayloadService
      .getAidaPayload()
      .pipe(
        skip(1),
        tap((aidaPayload) => {
          if (this.form.value.aida.enabled !== aidaPayload.enabled) {
            this._postActivityLogs('AIDA_TOGGLED', {
              from: this.form.value.aida.enabled,
              to: aidaPayload.enabled,
            });
          }
        })
      )
      .subscribe((aidaPayload) => {
        this.form.get('aida')?.patchValue(aidaPayload);
        this.form.get('is_aida_enabled')?.patchValue(aidaPayload.enabled);
        this.updatePayload({
          aida: aidaPayload,
          is_aida_enabled: aidaPayload.enabled,
        });

        if (aidaPayload.enabled) {
          const updatedPayload = {
            feot: {},
            is_feot_enabled: false,
            qbp: { enable: false },
            is_enable_margin_max: false,
            soft_launch: { enable: false },
          };

          this.form.patchValue(updatedPayload);
          this.updatePayload(updatedPayload);
        }
        if (
          !aidaPayload.enabled &&
          this.surveyDatails.ps_survey_status !== this.surveyStatus.Draft
        ) {
          if (this.surveyDatails.aida?.selected_quota_ids?.length) {
            this.form.patchValue({
              qbp: {
                enable: !this.form.value.qbp.enable,
                quota_ids: this.surveyDatails.aida.selected_quota_ids ?? [],
                selectedQuotaIds:
                  this.surveyDatails.aida.selected_quota_ids ?? [],
                category: this.surveyDatails.aida.category ?? '',
              },
            });
            this._setQbpSelectionDetails(this.form.value.qbp);
          }

          this.updatePayload({ qbp: this.form.value.qbp });
        }
      });
    this._subscriptions.add(_subscription);
  }

  get shouldDisableLiveTestUrlField(): boolean {
    const { uniqBuyerEntryLinkFlag, isSameBuyerUrlChecked } = this.form.value;
    this.updatePayload({
      is_same_buyer_url_checked: isSameBuyerUrlChecked,
    });
    return (
      this.disableLiveURLField ||
      uniqBuyerEntryLinkFlag ||
      (this.isTraditionalMcSurvey && isSameBuyerUrlChecked)
    );
  }

  private _setMultiCountrySurveyData() {
    if (this._isMcSvpSurvey()) {
      return;
    }
    const { live_url, test_url } = this.surveyPayload;
    this._multicountrySurveys.forEach((survey: MultiCountrySurvey) => {
      if (Number(survey.survey_id) === Number(this.surveyId)) {
        survey.live_url = live_url;
        survey.test_url = test_url;
      }
    });
    this._multiCountryDraftService.setMultiCountrySurveyData(
      this._multicountrySurveys
    );
  }

  private _clearUniqueEntryLinksMcSurveys() {
    if (this._isMcSvpSurvey()) {
      this._sendMcSpvDataForUpdate(
        { unique_links: { enable: false, items: [] } },
        true
      );
      this._sendMcSpvDataForUpdate(
        { unique_links: { enable: true, items: [] } },
        false
      ); //selected survey enable is true for validation on save
    } else if (this._isTraditionalMcSurvey()) {
      this._surveyMetaDataService.saveSurvey();
    }
  }

  private _shouldDisableQbp(isQbpEnabled: boolean): boolean {
    return this.surveyDatails.ps_survey_status !== SurveyStatus.Draft &&
      this.surveyDatails.aida?.enabled &&
      this.surveyDatails.aida?.selected_quota_ids?.length
      ? false
      : isQbpEnabled;
  }
}

export interface QbpSelectionQuota {
  category: string;
  name: string;
  selectedQuotaIds: Array<string>;
  qualificationData: AidaQualifications;
}

interface AidaEligibilityInfo {
  eligibility: AidaSurveyEligibility;
  features: SurveyFeaturesState;
}
